import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { forceCenter, forceCollide, forceManyBody } from 'd3-force';
import ReactMarkdown from 'react-markdown';
import './gatlas.css';
import './gatlas-light.css';
import { ensureGAtlasCookie, getCookieDaysLeft, getCookieId } from './WebserverUser';
import { getApiBaseUrl, getApiEndpoint } from './config/gatlas-config';
import {
  createServiceUpload,
  defaultServicePipeline,
  loadServiceCatalogue,
  loadServiceJobs,
  openJobDownload,
  serviceApiBase,
  webserverFetch,
} from '../shared/xomics-webserver-client';
import WebserverDemoSection from '../shared/WebserverDemoSection';

import baseLogo from '../../base_logo.png';
import baseLogoLight from '../../base_logo_light.png';
import gatlasLogo from '../../gphenome.png';
import gatlasLogoDark from '../../gphenome_dark.png';

const THEME_STORAGE_KEY = 'gatlas-theme-preference';
const GWAS_SERVICE_CODE = 'ggwas';

const atlasFetch = (url, options = {}) => {
  const token = getCookieId();
  if (!token) return Promise.reject(new Error('A G-Atlas credential is required'));
  return webserverFetch(url, token, options);
};

const formatNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

const formatCompact = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(num);
};

const formatScore = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return num.toFixed(3);
};

const buildApiUrl = (endpoint, params = {}) => {
  const url = new URL(getApiEndpoint(endpoint));
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

const sidebarGroups = [
  {
    id: 'main',
    title: 'G-GWAS',
    items: [
      { id: 'home', label: 'Overview', icon: 'home' },
      { id: 'atlas', label: 'Global Atlas', icon: 'globe' },
      { id: 'upload', label: 'My GWAS', icon: 'upload' },
    ],
  },
  {
    id: 'evidence',
    title: 'Evidence',
    items: [
      { id: 'mechanisms', label: 'Mechanisms', icon: 'project-diagram' },
      { id: 'loci', label: 'Loci', icon: 'dna' },
      { id: 'genes', label: 'Genes', icon: 'network-wired' },
    ],
  },
  {
    id: 'system',
    title: 'Workspace',
    items: [
      { id: 'api', label: 'API', icon: 'code' },
      { id: 'help', label: 'Help & Docs', icon: 'book' },
      { id: 'settings', label: 'Settings', icon: 'cog' },
    ],
  },
];

const AXIS_COLORS = {
  neural_enriched: '#5eead4',
  systemic_non_neural: '#fb923c',
  systemic_other: '#f59e0b',
  neuro_systemic: '#c084fc',
  mixed_trait_axis: '#f472b6',
  unknown: '#94a3b8',
};

const AXIS_ORDER = [
  'neural_enriched',
  'systemic_non_neural',
  'systemic_other',
  'neuro_systemic',
  'mixed_trait_axis',
];

const AXIS_LABELS = {
  neural_enriched: 'Brain & nerve',
  systemic_non_neural: 'Cardio · metabolic · immune',
  systemic_other: 'Other systemic',
  neuro_systemic: 'Neuro-systemic',
  mixed_trait_axis: 'Pleiotropic',
  unknown: 'Unassigned',
};

const AXIS_SHORT = {
  neural_enriched: 'Brain',
  systemic_non_neural: 'Cardio/Metabolic',
  systemic_other: 'Other systemic',
  neuro_systemic: 'Neuro-systemic',
  mixed_trait_axis: 'Pleiotropic',
};

const EVIDENCE_CHIPS = [
  { id: 'has_candidate', label: 'Has candidate', color: '#fb923c', metric: 'has_candidate', desc: 'Variant with prioritized candidate gene' },
  { id: 'external', label: 'External evidence', color: '#5eead4', metric: 'any_external_support', desc: 'Has Open Targets / eQTL / ABC support' },
  { id: 'functional', label: 'Functional support', color: '#facc15', metric: 'any_public_functional_support', desc: 'MPRA / CRISPR / MPRAVarDB hit' },
];

const NETWORK_COLORS = {
  variant: '#90b4e8',
  locus: '#fb923c',
  gene: '#5eead4',
  evidence: '#f472b6',
  pathway: '#c084fc',
  trait: '#facc15',
  axis: '#a855f7',
  score_band: '#facc15',
  default: '#94a3b8',
};

const LAYERS = [
  { id: 'pathway', label: 'Pathways', color: '#c084fc', desc: 'Reactome biology hubs' },
  { id: 'trait', label: 'Traits', color: '#facc15', desc: 'GWAS trait hubs (EBI Catalog)' },
  { id: 'locus', label: 'Loci', color: '#fb923c', desc: '500 kb genomic windows' },
  { id: 'evidence', label: 'Evidence', color: '#f472b6', desc: 'L2G / e2G / coloc / eQTL / ABC / pQTL' },
  { id: 'variant', label: 'Top variant', color: '#90b4e8', desc: 'Top-scoring variant per gene' },
];

const axisOf = (node) => {
  const a = node?.metrics?.primary_axis;
  if (!a || !AXIS_COLORS[a]) return 'unknown';
  return a;
};

const colorForGene = (node) => AXIS_COLORS[axisOf(node)] || AXIS_COLORS.unknown;

const radiusFromScore = (score) => {
  const s = Number(score) || 0;
  // Clamp 0..~3000-ish range from mechanism_priority_score / priority_score
  const norm = Math.min(1, Math.log10(1 + Math.max(0, s)) / 3.4);
  return 4 + norm * 7;
};

const GAtlasForceNetwork = ({
  networkData,
  loading,
  axisFilter,
  evidenceFilter,
  pathwayGeneSet,
  searchQuery,
  selectedId,
  onNodeClick,
}) => {
  const containerRef = useRef(null);
  const fgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoverNode, setHoverNode] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const el = containerRef.current;
    const update = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setDimensions({ width: rect.width, height: rect.height });
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const graphData = useMemo(() => {
    const rawNodes = networkData?.nodes || [];
    const rawEdges = networkData?.edges || [];
    const nodes = rawNodes.map((node) => {
      const score = node.metrics?.mechanism_priority_score || node.metrics?.priority_score || node.metrics?.max_score || 0;
      const radius = node.type === 'gene'
        ? radiusFromScore(score)
        : node.type === 'locus' ? 5
        : node.type === 'evidence' ? 6
        : node.type === 'pathway' ? 9
        : node.type === 'axis' ? 6
        : Math.max(3, Math.min(7, 3 + Number(node.metrics?.max_score || 0) * 8));
      return {
        ...node,
        label: node.label || node.id,
        val: radius,
      };
    });
    const links = rawEdges.map((edge, index) => ({
      ...edge,
      id: `${edge.source}-${edge.target}-${edge.type}-${index}`,
    }));
    return { nodes, links };
  }, [networkData]);

  // 2-hop neighborhood for the selected node.
  // direct: selected ∪ first-degree neighbors (full brightness)
  // second: neighbors-of-neighbors not already in `direct` (intermediate brightness)
  const neighborSet = useMemo(() => {
    if (!selectedId) return null;
    const direct = new Set([selectedId]);
    const edges = networkData?.edges || [];
    for (const e of edges) {
      const a = typeof e.source === 'object' ? e.source.id : e.source;
      const b = typeof e.target === 'object' ? e.target.id : e.target;
      if (a === selectedId) direct.add(b);
      if (b === selectedId) direct.add(a);
    }
    const second = new Set();
    for (const e of edges) {
      const a = typeof e.source === 'object' ? e.source.id : e.source;
      const b = typeof e.target === 'object' ? e.target.id : e.target;
      if (direct.has(a) && !direct.has(b)) second.add(b);
      if (direct.has(b) && !direct.has(a)) second.add(a);
    }
    return { direct, second };
  }, [selectedId, networkData]);

  // Organic force layout — no radial sectors. Axis is encoded by color only.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg || !graphData.nodes.length) return;

    fg.d3Force('axisRadial', null);
    fg.d3Force('charge', forceManyBody().strength((node) => {
      if (node.type === 'evidence' || node.type === 'axis') return -260;
      if (node.type === 'gene') return -180;
      return -70;
    }));
    fg.d3Force('collide', forceCollide((node) => Math.max(10, (node.val || 4) * 2)));
    fg.d3Force('center', forceCenter(0, 0));
    fg.d3Force('link').distance((link) => {
      if (link.type === 'shared_locus') return 70;
      if (link.type === 'candidate_gene') return 80;
      if (link.type === 'supported_by') return 120;
      if (link.type === 'locus_axis') return 160;
      return 64;
    }).strength((link) => {
      if (link.type === 'shared_locus') return 0.32;
      if (link.type === 'candidate_gene') return 0.42;
      if (link.type === 'supported_by') return 0.16;
      if (link.type === 'locus_axis') return 0.06;
      return 0.28;
    });
  }, [graphData]);

  useEffect(() => {
    if (!fgRef.current || !graphData.nodes.length) return;
    if (selectedId) return; // focus effect handles framing
    const id = setTimeout(() => fgRef.current?.zoomToFit(700, 80), 1200);
    return () => clearTimeout(id);
  }, [graphData.nodes.length, selectedId]);

  // Pan + zoom to the selected node whenever selection changes — from any source.
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    const start = Date.now();
    const tryFocus = () => {
      if (cancelled) return;
      const fg = fgRef.current;
      if (!fg) {
        setTimeout(tryFocus, 120);
        return;
      }
      const node = graphData.nodes.find((n) => n.id === selectedId);
      if (!node) {
        // Selected node not in current graph yet — likely still fetching after a
        // layer toggle. Retry briefly.
        if (Date.now() - start < 4000) setTimeout(tryFocus, 150);
        return;
      }
      if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) {
        if (Date.now() - start < 4000) setTimeout(tryFocus, 100);
        return;
      }
      // Settle zoom first so we know the pixel↔graph ratio before computing the offset.
      const target = 0.9;
      const cur = fg.zoom();
      if (cur < target * 0.95) fg.zoom(target, 700);
      const effectiveZoom = Math.max(cur, target);

      // The detail panel sits on the right side of the canvas. centerAt() places
      // the node at the canvas geometric center — but visually that's behind the
      // panel. Shift the target right (in graph coords) by half the panel width
      // so the node lands in the actual visible center.
      const panelEl = document.querySelector('.GAtlas-detail.open');
      const panelPx = panelEl ? panelEl.getBoundingClientRect().width : 0;
      const offsetX = (panelPx / 2) / effectiveZoom;
      fg.centerAt(node.x + offsetX, node.y, 700);
    };
    tryFocus();
    return () => { cancelled = true; };
  }, [selectedId, graphData.nodes]);

  const matchesSearch = useCallback((node) => {
    if (!searchQuery) return false;
    const q = searchQuery.toLowerCase();
    return String(node.label || '').toLowerCase().includes(q) || String(node.id || '').toLowerCase().includes(q);
  }, [searchQuery]);

  const passesAxisFilter = useCallback((node) => {
    if (!axisFilter || axisFilter.size === 0) return true;
    // Filter applies to anything that carries an axis (genes, loci, variants).
    // Evidence/axis hub nodes always pass (they're connectors).
    if (node.type === 'evidence' || node.type === 'axis' || node.type === 'score_band') return true;
    return axisFilter.has(axisOf(node));
  }, [axisFilter]);

  const passesEvidenceFilter = useCallback((node) => {
    if (!evidenceFilter || evidenceFilter.size === 0) return true;
    if (node.type === 'evidence' || node.type === 'axis' || node.type === 'score_band') return true;
    const m = node.metrics || {};
    for (const id of evidenceFilter) {
      const chip = EVIDENCE_CHIPS.find((c) => c.id === id);
      if (!chip) continue;
      if (!Number(m[chip.metric])) return false;
    }
    return true;
  }, [evidenceFilter]);

  const passesPathwayFilter = useCallback((node) => {
    if (!pathwayGeneSet) return true;
    // Pathway filter applies to gene-level nodes; non-gene types pass through
    // (a variant whose top_gene is in the union also passes).
    if (node.type === 'gene') return pathwayGeneSet.has(node.label);
    if (node.type === 'pathway') return true;
    if (node.type === 'variant') {
      const tg = node.metrics?.top_gene;
      if (tg && pathwayGeneSet.has(tg)) return true;
      return false;
    }
    if (node.type === 'locus') return true;
    return true;
  }, [pathwayGeneSet]);

  const isHighlighted = useCallback(() => false, []);

  const colorFor = useCallback((node) => {
    // Axis-driven color for genes/variants/loci when an axis is known.
    if ((node.type === 'gene' || node.type === 'variant' || node.type === 'locus')
        && node.metrics?.primary_axis) {
      return colorForGene(node);
    }
    return NETWORK_COLORS[node.type] || NETWORK_COLORS.default;
  }, []);

  const drawNode = useCallback((node, ctx, globalScale) => {
    const radius = Math.max(3, Number(node.val || 4));
    const color = colorFor(node);
    const isHovered = hoverNode?.id === node.id;
    const isSelected = selectedId === node.id;
    const matched = matchesSearch(node);
    const axisOk = passesAxisFilter(node);
    const evOk = passesEvidenceFilter(node);
    const pwOk = passesPathwayFilter(node);

    // Two-hop neighborhood: direct = full bright, second-hop = intermediate, rest = dim
    let neighborTier = 'all'; // 'direct' | 'second' | 'far'
    if (neighborSet) {
      if (neighborSet.direct.has(node.id)) neighborTier = 'direct';
      else if (neighborSet.second.has(node.id)) neighborTier = 'second';
      else neighborTier = 'far';
    }
    const filterFails = (!axisOk) || (!evOk) || (!pwOk);
    const searchFails = searchQuery && !matched && !isHovered && !isSelected;
    const farFails = neighborTier === 'far' && !isHovered;
    const dim = filterFails || farFails || searchFails;
    const halo = node.type === 'gene' && Number(node.metrics?.any_public_functional_support) > 0;
    let alpha = 1;
    if (dim) alpha = 0.18;
    else if (neighborTier === 'second') alpha = 0.55;

    ctx.save();
    ctx.globalAlpha = alpha;

    // Halo ring for highlighted (functional support / multi-locus)
    if (halo) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius + 3, 0, Math.PI * 2);
      ctx.strokeStyle = '#d6a643';
      ctx.lineWidth = 1.4;
      ctx.stroke();
    }

    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();

    if (isSelected) {
      ctx.lineWidth = 2.4;
      ctx.strokeStyle = '#ffffff';
    } else if (isHovered || matched) {
      ctx.lineWidth = 1.8;
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    } else {
      ctx.lineWidth = 0.8;
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    }
    ctx.stroke();

    // Labels: very sparse by default. Only on hover, selection, search match,
    // sufficiently-large hub genes, or when the user has zoomed in.
    const shouldLabel =
      isHovered ||
      isSelected ||
      matched ||
      (node.type === 'gene' && radius >= 7) ||
      (node.type === 'gene' && globalScale > 1.6) ||
      (node.type === 'locus' && globalScale > 2.2);

    if (shouldLabel && !dim) {
      const label = String(node.label || '').slice(0, 24);
      const fontSize = Math.max(7, 10 / Math.max(globalScale, 0.75));
      ctx.font = `${(isSelected || matched || isHovered) ? 600 : 500} ${fontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(20, 20, 20, 0.92)';
      ctx.strokeText(label, node.x + radius + 4, node.y);
      ctx.fillStyle = isSelected ? '#ffffff' : 'rgba(224, 224, 224, 0.92)';
      ctx.fillText(label, node.x + radius + 4, node.y);
    }
    ctx.restore();
  }, [hoverNode, selectedId, neighborSet, matchesSearch, passesAxisFilter, passesEvidenceFilter, passesPathwayFilter, searchQuery, colorFor]);

  const linkTier = useCallback((link) => {
    if (!selectedId || !neighborSet) return 'all';
    const a = typeof link.source === 'object' ? link.source.id : link.source;
    const b = typeof link.target === 'object' ? link.target.id : link.target;
    if (a === selectedId || b === selectedId) return 'direct';
    if (neighborSet.direct.has(a) && neighborSet.direct.has(b)) return 'direct';
    const ahit = neighborSet.direct.has(a) || neighborSet.second.has(a);
    const bhit = neighborSet.direct.has(b) || neighborSet.second.has(b);
    if (ahit && bhit) return 'second';
    return 'far';
  }, [selectedId, neighborSet]);

  const linkColor = useCallback((link) => {
    const tier = linkTier(link);
    const factor = tier === 'direct' ? 2.2 : tier === 'second' ? 1.0 : tier === 'far' ? 0.18 : 1.0;
    const a = (base) => Math.min(0.95, base * factor).toFixed(2);
    const rgba = (r, g, b, base) => `rgba(${r},${g},${b},${a(base)})`;
    if (link.type === 'shared_locus') return rgba(148, 163, 184, 0.22);
    if (link.type === 'candidate_gene') return rgba(94, 234, 212, 0.42);
    if (link.type === 'in_pathway') return rgba(192, 132, 252, 0.32);
    if (link.type === 'hits_trait') return rgba(250, 204, 21, 0.36);
    if (link.type === 'supported_by') return rgba(192, 132, 252, 0.32);
    if (link.type === 'locus_axis') return rgba(168, 85, 247, 0.22);
    if (link.type === 'score_band') return rgba(250, 204, 21, 0.2);
    return rgba(148, 163, 184, 0.2);
  }, [linkTier]);

  return (
    <div className="GAtlas-force-network" ref={containerRef}>
      {graphData.nodes.length > 0 && dimensions.width > 0 ? (
        <ForceGraph2D
          ref={fgRef}
          width={dimensions.width}
          height={dimensions.height}
          graphData={graphData}
          nodeId="id"
          nodeLabel={(node) => {
            if (node.type === 'gene') {
              const m = node.metrics || {};
              return `${node.label} • ${AXIS_LABELS[axisOf(node)]} • ${Number(m.n_traits) || 0} traits • score ${formatScore(m.mechanism_priority_score || m.priority_score || 0)}`;
            }
            return `${node.type}: ${node.label}`;
          }}
          nodeVal="val"
          nodeCanvasObject={drawNode}
          linkSource="source"
          linkTarget="target"
          linkColor={linkColor}
          linkWidth={(link) => {
            const base = link.type === 'candidate_gene' ? 1.6
              : link.type === 'supported_by' ? 1.2
              : link.type === 'in_pathway' ? 1.2
              : link.type === 'hits_trait' ? 1.1
              : 0.8;
            const tier = linkTier(link);
            if (tier === 'direct') return base * 2.2;
            if (tier === 'second') return base * 1.2;
            return base;
          }}
          onNodeHover={setHoverNode}
          onNodeClick={onNodeClick}
          cooldownTicks={70}
          d3AlphaDecay={0.05}
          d3VelocityDecay={0.4}
          warmupTicks={30}
          backgroundColor="rgba(0,0,0,0)"
        />
      ) : (
        <div className="GAtlas-network-empty">
          <i className="fas fa-project-diagram"></i>
          <span>{loading ? 'Loading network…' : 'No data for current filters'}</span>
        </div>
      )}
    </div>
  );
};

const GAtlasDetailPanel = ({ selection, onClose, onNavigate, networkData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('summary');

  useEffect(() => {
    if (!selection) return undefined;
    setTab('summary');
    if (selection.kind === 'pathway' || selection.kind === 'trait' || selection.kind === 'evidence') {
      setData(null); setLoading(false); setError('');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);
    const endpointKey = selection.kind === 'gene' ? 'atlasGene'
      : selection.kind === 'locus' ? 'atlasLocus'
      : 'atlasVariant';
    const url = `${getApiEndpoint(endpointKey)}/${encodeURIComponent(selection.id)}`;
    atlasFetch(url).then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (!cancelled) setData(d);
    }).catch((err) => {
      if (!cancelled) setError(err.message);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [selection]);

  if (!selection) return null;

  const headerColor = selection.kind === 'gene'
    ? (AXIS_COLORS[selection.axis] || AXIS_COLORS.unknown)
    : selection.kind === 'locus' ? '#fb923c'
    : '#90b4e8';

  const renderGene = () => {
    if (!data) return null;
    const candidates = data.candidates || [];
    const variants = data.variants || [];
    const traits = data.traits || [];
    const pathways = data.pathways || [];
    const loci = Array.from(new Set(candidates.map((c) => c.locus_500kb))).filter(Boolean);
    const axes = Array.from(new Set(candidates.map((c) => c.primary_axis).filter(Boolean)));
    const topPriority = candidates.reduce((m, c) => Math.max(m, Number(c.priority_score) || 0), 0);
    const functional = candidates.some((c) => Number(c.any_public_functional_support));

    return (
      <>
        <div className="GAtlas-detail-summary">
          <div><strong>{loci.length}</strong><span>loci</span></div>
          <div><strong>{traits.length}</strong><span>distinct traits</span></div>
          <div><strong>{pathways.length}</strong><span>pathways</span></div>
        </div>
        <div className="GAtlas-detail-tabs">
          <button className={`GAtlas-detail-tab ${tab === 'summary' ? 'active' : ''}`} onClick={() => setTab('summary')}>Overview</button>
          <button className={`GAtlas-detail-tab ${tab === 'traits' ? 'active' : ''}`} onClick={() => setTab('traits')}>Traits</button>
          <button className={`GAtlas-detail-tab ${tab === 'pathways' ? 'active' : ''}`} onClick={() => setTab('pathways')}>Pathways</button>
          <button className={`GAtlas-detail-tab ${tab === 'loci' ? 'active' : ''}`} onClick={() => setTab('loci')}>Loci</button>
          <button className={`GAtlas-detail-tab ${tab === 'variants' ? 'active' : ''}`} onClick={() => setTab('variants')}>Variants</button>
        </div>
        <div className="GAtlas-detail-body">
          {tab === 'summary' && (
            <>
              <section>
                <h5>Trait axes</h5>
                {axes.length ? axes.map((a) => (
                  <span key={a} className="GAtlas-pill" style={{ color: AXIS_COLORS[a] || AXIS_COLORS.unknown, borderColor: 'currentColor' }}>{AXIS_LABELS[a] || a}</span>
                )) : <span className="GAtlas-detail-empty">No axis assignment</span>}
              </section>
              <section>
                <h5>Top traits</h5>
                {traits.length === 0 ? <span className="GAtlas-detail-empty">No GWAS traits found</span> : (
                  <table>
                    <tbody>
                      {traits.slice(0, 6).map((t) => (
                        <tr key={t.gcst}>
                          <td>{t.trait || t.mapped_trait || t.gcst}</td>
                          <td style={{ textAlign: 'right' }}>{formatScore(t.max_score)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
              <section>
                <h5>Top pathways (Reactome)</h5>
                {pathways.length === 0 ? <span className="GAtlas-detail-empty">No Reactome pathway membership</span> : (
                  <table>
                    <tbody>
                      {pathways.slice(0, 5).map((p) => (
                        <tr key={p.pathway_id}>
                          <td>{p.pathway_name}</td>
                          <td style={{ textAlign: 'right' }}>{p.recurring_genes}/{p.total_genes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
              <section>
                <h5>Priority</h5>
                <div>Top priority score: <strong>{formatScore(topPriority)}</strong></div>
                <div>Public functional support: <strong>{functional ? 'yes' : 'no'}</strong></div>
              </section>
            </>
          )}
          {tab === 'traits' && (
            traits.length === 0 ? <span className="GAtlas-detail-empty">No GWAS traits found for this gene's variants</span> : (
              <table>
                <thead><tr><th>Trait</th><th>Max score</th><th>Variants</th></tr></thead>
                <tbody>
                  {traits.map((t) => (
                    <tr key={t.gcst}>
                      <td title={t.gcst}>{t.trait || t.mapped_trait || t.gcst}</td>
                      <td>{formatScore(t.max_score)}</td>
                      <td>{t.n_variants}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
          {tab === 'pathways' && (
            pathways.length === 0 ? <span className="GAtlas-detail-empty">No Reactome pathways found for this gene</span> : (
              <table>
                <thead><tr><th>Pathway</th><th>Cohort genes</th><th>Total</th></tr></thead>
                <tbody>
                  {pathways.map((p) => (
                    <tr key={p.pathway_id}>
                      <td>{p.pathway_name}{p.is_leaf ? '' : ' (group)'}</td>
                      <td>{p.recurring_genes}</td>
                      <td>{p.total_genes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
          {tab === 'loci' && (
            <table>
              <thead><tr><th>Locus</th><th>SNPs</th><th>Top score</th></tr></thead>
              <tbody>
                {loci.map((l) => {
                  const rows = candidates.filter((c) => c.locus_500kb === l);
                  const top = Math.max(...rows.map((r) => Number(r.priority_score) || 0));
                  return (
                    <tr key={l}>
                      <td><button className="linklike" onClick={() => onNavigate({ kind: 'locus', id: l })}>{l}</button></td>
                      <td>{rows.length}</td>
                      <td>{formatScore(top)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {tab === 'variants' && (
            <table>
              <thead><tr><th>SNP</th><th>Locus</th><th>Score</th><th>Traits</th></tr></thead>
              <tbody>
                {variants.slice(0, 80).map((v) => (
                  <tr key={v.snp}>
                    <td><button className="linklike" onClick={() => onNavigate({ kind: 'variant', id: v.snp })}>{v.snp}</button></td>
                    <td>{v.locus_500kb}</td>
                    <td>{formatScore(v.max_score)}</td>
                    <td>{v.n_traits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    );
  };

  const renderLocus = () => {
    if (!data) return null;
    const candidates = data.candidates || [];
    const variants = data.variants || [];
    const genes = Array.from(new Set(candidates.map((c) => c.gene_symbol)));
    const topVariant = variants[0];
    return (
      <>
        <div className="GAtlas-detail-summary">
          <div><strong>{genes.length}</strong><span>candidate genes</span></div>
          <div><strong>{variants.length}</strong><span>variants</span></div>
          <div><strong>{topVariant ? formatScore(topVariant.max_score) : '-'}</strong><span>top score</span></div>
        </div>
        <div className="GAtlas-detail-tabs">
          <button className={`GAtlas-detail-tab ${tab === 'summary' ? 'active' : ''}`} onClick={() => setTab('summary')}>Overview</button>
          <button className={`GAtlas-detail-tab ${tab === 'genes' ? 'active' : ''}`} onClick={() => setTab('genes')}>Genes</button>
          <button className={`GAtlas-detail-tab ${tab === 'variants' ? 'active' : ''}`} onClick={() => setTab('variants')}>Variants</button>
        </div>
        <div className="GAtlas-detail-body">
          {tab === 'summary' && (
            <section>
              <h5>Candidate gene rows</h5>
              <table>
                <thead><tr><th>Gene</th><th>SNP</th><th>Score</th><th>Class</th></tr></thead>
                <tbody>
                  {candidates.slice(0, 20).map((c) => (
                    <tr key={`${c.gene_symbol}-${c.SNP}`}>
                      <td><button className="linklike" onClick={() => onNavigate({ kind: 'gene', id: c.gene_symbol })}>{c.gene_symbol}</button></td>
                      <td><button className="linklike" onClick={() => onNavigate({ kind: 'variant', id: c.SNP })}>{c.SNP}</button></td>
                      <td>{formatScore(c.priority_score)}</td>
                      <td>{c.candidate_class || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          {tab === 'genes' && (
            <table>
              <thead><tr><th>Gene</th><th>Top score</th></tr></thead>
              <tbody>
                {genes.map((g) => {
                  const top = Math.max(...candidates.filter((c) => c.gene_symbol === g).map((c) => Number(c.priority_score) || 0));
                  return (
                    <tr key={g}>
                      <td><button className="linklike" onClick={() => onNavigate({ kind: 'gene', id: g })}>{g}</button></td>
                      <td>{formatScore(top)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {tab === 'variants' && (
            <table>
              <thead><tr><th>SNP</th><th>Score</th><th>Traits</th><th>Candidate</th></tr></thead>
              <tbody>
                {variants.slice(0, 80).map((v) => (
                  <tr key={v.snp}>
                    <td><button className="linklike" onClick={() => onNavigate({ kind: 'variant', id: v.snp })}>{v.snp}</button></td>
                    <td>{formatScore(v.max_score)}</td>
                    <td>{v.n_traits}</td>
                    <td>{v.has_candidate ? 'yes' : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    );
  };

  const renderVariant = () => {
    if (!data) return null;
    const v = data.variant;
    const evidence = data.evidence || [];
    const candidates = data.candidates || [];
    const traits = data.traits || [];
    if (!v) return <div className="GAtlas-detail-empty">No variant record found</div>;
    return (
      <>
        <div className="GAtlas-detail-summary">
          <div><strong>{formatScore(v.max_score)}</strong><span>max score</span></div>
          <div><strong>{v.n_traits || 0}</strong><span>traits</span></div>
          <div><strong>{v.candidate_gene_count || 0}</strong><span>cand. genes</span></div>
        </div>
        <div className="GAtlas-detail-tabs">
          <button className={`GAtlas-detail-tab ${tab === 'summary' ? 'active' : ''}`} onClick={() => setTab('summary')}>Overview</button>
          <button className={`GAtlas-detail-tab ${tab === 'traits' ? 'active' : ''}`} onClick={() => setTab('traits')}>Traits</button>
          <button className={`GAtlas-detail-tab ${tab === 'evidence' ? 'active' : ''}`} onClick={() => setTab('evidence')}>Evidence</button>
        </div>
        <div className="GAtlas-detail-body">
          {tab === 'summary' && (
            <>
              <section>
                <h5>Position</h5>
                <div>{v.chr}:{v.bp} • locus <button className="linklike" onClick={() => onNavigate({ kind: 'locus', id: v.locus_500kb })}>{v.locus_500kb}</button></div>
              </section>
              <section>
                <h5>Score band &amp; axis</h5>
                <div>Band: {v.score_band}</div>
                <div>Primary axis: {v.primary_axis ? (AXIS_LABELS[v.primary_axis] || v.primary_axis) : 'unassigned'}</div>
                <div>Recurrent: {v.is_recurrent ? 'yes' : 'no'}</div>
              </section>
              <section>
                <h5>Top traits hit</h5>
                {traits.length === 0 ? <span className="GAtlas-detail-empty">No GWAS trait hits</span> : (
                  <table>
                    <tbody>
                      {traits.slice(0, 8).map((t) => (
                        <tr key={t.gcst}>
                          <td>{t.trait || t.mapped_trait || t.gcst}</td>
                          <td style={{ textAlign: 'right' }}>{formatScore(t.score)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
              <section>
                <h5>Candidate genes</h5>
                {candidates.length ? (
                  <table>
                    <thead><tr><th>Gene</th><th>Locus</th><th>Score</th></tr></thead>
                    <tbody>
                      {candidates.map((c) => (
                        <tr key={`${c.gene_symbol}-${c.locus_500kb}`}>
                          <td><button className="linklike" onClick={() => onNavigate({ kind: 'gene', id: c.gene_symbol })}>{c.gene_symbol}</button></td>
                          <td>{c.locus_500kb}</td>
                          <td>{formatScore(c.priority_score)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <span className="GAtlas-detail-empty">No prioritized candidate gene rows</span>
                )}
              </section>
            </>
          )}
          {tab === 'traits' && (
            traits.length === 0 ? <span className="GAtlas-detail-empty">No GWAS trait hits</span> : (
              <table>
                <thead><tr><th>Trait</th><th>Score</th><th>Author</th></tr></thead>
                <tbody>
                  {traits.map((t) => (
                    <tr key={t.gcst}>
                      <td title={`${t.gcst}${t.pubmed_id ? ' • PMID ' + t.pubmed_id : ''}`}>{t.trait || t.mapped_trait || t.gcst}</td>
                      <td>{formatScore(t.score)}</td>
                      <td>{t.first_author || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
          {tab === 'evidence' && (
            evidence.length ? (
              <table>
                <thead><tr><th>Source</th><th>Detail</th></tr></thead>
                <tbody>
                  {evidence.map((e, i) => (
                    <tr key={i}>
                      <td>{e.source || e.evidence_source || e.type || '—'}</td>
                      <td>{e.detail || e.gene_symbol || e.value || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <span className="GAtlas-detail-empty">No external evidence rows for this variant</span>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="GAtlas-detail open">
      <div className="GAtlas-detail-header">
        <span className="swatch" style={{ background: headerColor }}></span>
        <h3>{selection.id}</h3>
        <span className="kind">{selection.kind}</span>
        <button className="GAtlas-detail-close" onClick={onClose}><i className="fas fa-times"></i></button>
      </div>
      {loading && <div className="GAtlas-detail-body">Loading…</div>}
      {error && <div className="GAtlas-detail-body"><strong>{error}</strong></div>}
      {!loading && !error && (
        <>
          {selection.kind === 'gene' && renderGene()}
          {selection.kind === 'locus' && renderLocus()}
          {selection.kind === 'variant' && renderVariant()}
          {(selection.kind === 'pathway' || selection.kind === 'trait' || selection.kind === 'evidence') && (() => {
            const id = selection.nodeId;
            const edges = networkData?.edges || [];
            const nodes = networkData?.nodes || [];
            const nodeById = new Map(nodes.map((n) => [n.id, n]));
            const connectedIds = [];
            for (const e of edges) {
              const a = typeof e.source === 'object' ? e.source.id : e.source;
              const b = typeof e.target === 'object' ? e.target.id : e.target;
              if (a === id) connectedIds.push(b);
              else if (b === id) connectedIds.push(a);
            }
            const connectedGenes = connectedIds
              .map((nid) => nodeById.get(nid))
              .filter((n) => n && n.type === 'gene');
            const headerLabel = selection.kind === 'pathway' ? 'Reactome pathway hub'
              : selection.kind === 'trait' ? 'GWAS trait hub'
              : 'Evidence source';
            const summaryNote = selection.kind === 'pathway'
              ? 'Candidate genes mapped into this Reactome pathway.'
              : selection.kind === 'trait' ? 'Candidate genes whose variants recur on this trait.'
              : 'Candidate genes supported by this evidence source.';
            const meta = nodeById.get(id)?.metrics || {};
            return (
              <>
                <div className="GAtlas-detail-summary">
                  <div><strong>{connectedGenes.length}</strong><span>connected genes</span></div>
                  <div><strong>{meta.variant_count || meta.recurring_genes || meta.gene_count || '-'}</strong><span>{selection.kind === 'trait' ? 'variants' : selection.kind === 'evidence' ? 'genes hit' : 'recurring'}</span></div>
                  <div><strong>{selection.kind === 'trait' ? (meta.max_score ? formatScore(meta.max_score) : '-') : selection.kind === 'evidence' ? 'support' : (meta.is_leaf === 0 ? 'group' : 'leaf')}</strong><span>{selection.kind === 'trait' ? 'max score' : selection.kind === 'evidence' ? 'type' : 'level'}</span></div>
                </div>
                <div className="GAtlas-detail-body">
                  <section>
                    <h5>{headerLabel}</h5>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.2vh', lineHeight: 1.5, margin: 0 }}>
                      {summaryNote}
                    </p>
                  </section>
                  <section>
                    <h5>Connected genes</h5>
                    {connectedGenes.length === 0 ? <span className="GAtlas-detail-empty">No connected genes in current view</span> : (
                      <table>
                        <thead><tr><th>Gene</th><th>Axis</th><th>Score</th></tr></thead>
                        <tbody>
                          {connectedGenes
                            .slice()
                            .sort((a, b) => (Number(b.metrics?.mechanism_priority_score || b.metrics?.priority_score || 0)) - (Number(a.metrics?.mechanism_priority_score || a.metrics?.priority_score || 0)))
                            .map((g) => (
                              <tr key={g.id}>
                                <td>
                                  <button className="linklike" onClick={() => onNavigate({ kind: 'gene', id: g.label, axis: g.metrics?.primary_axis, nodeId: g.id })}>
                                    {g.label}
                                  </button>
                                </td>
                                <td>{g.metrics?.primary_axis ? (AXIS_SHORT[g.metrics.primary_axis] || g.metrics.primary_axis) : '-'}</td>
                                <td>{formatScore(g.metrics?.mechanism_priority_score || g.metrics?.priority_score || 0)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </section>
                </div>
              </>
            );
          })()}
        </>
      )}
    </div>
  );
};

const GAtlasTopRail = ({
  tabs,
  tab,
  setTab,
  sort,
  setSort,
  nodesByType,
  sortFor,
  selection,
  setSelection,
  networkData,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const tabToType = { genes: 'gene', pathways: 'pathway', traits: 'trait', loci: 'locus', evidence: 'evidence', variants: 'variant' };
  const kind = tabToType[tab] || 'gene';
  const items = (nodesByType[kind] || []).slice().sort(sortFor(kind, sort)).slice(0, 30);

  if (collapsed) {
    return (
      <button
        className="GAtlas-top-rail-collapsed"
        title="Show top hits"
        onClick={() => setCollapsed(false)}
      >
        <i className="fas fa-list-ol"></i>
        <span>Top hits</span>
        <i className="fas fa-chevron-up"></i>
      </button>
    );
  }

  // Stats for footer
  const totalNodes = (networkData?.nodes || []).length;
  const totalEdges = (networkData?.edges || []).length;
  let neighborCount = 0;
  if (selection) {
    const sid = selection.nodeId || `${selection.kind}:${selection.id}`;
    for (const e of (networkData?.edges || [])) {
      const a = typeof e.source === 'object' ? e.source.id : e.source;
      const b = typeof e.target === 'object' ? e.target.id : e.target;
      if (a === sid || b === sid) neighborCount++;
    }
  }

  const renderRow = (n) => {
    const sid = selection ? (selection.nodeId || `${selection.kind}:${selection.id}`) : null;
    const isSelected = n.id === sid;
    const m = n.metrics || {};
    const onClick = () => {
      if (n.type === 'gene') setSelection({ kind: 'gene', id: n.label, axis: m.primary_axis, nodeId: n.id });
      else if (n.type === 'pathway') setSelection({ kind: 'pathway', id: n.label, nodeId: n.id });
      else if (n.type === 'trait') setSelection({ kind: 'trait', id: n.label, nodeId: n.id, gcst: m.gcst_id });
      else if (n.type === 'locus') setSelection({ kind: 'locus', id: n.label, nodeId: n.id });
      else if (n.type === 'evidence') setSelection({ kind: 'evidence', id: n.label, nodeId: n.id });
      else if (n.type === 'variant') setSelection({ kind: 'variant', id: n.label, nodeId: n.id });
    };
    let primary; let secondary;
    if (n.type === 'gene') {
      const axis = AXIS_COLORS[m.primary_axis] || AXIS_COLORS.unknown;
      primary = <span className="name"><span className="swatch" style={{ background: axis }}></span>{n.label}</span>;
      secondary = `${Number(m.n_traits) || 0}t · ${formatScore(m.mechanism_priority_score || m.priority_score || 0)}`;
    } else if (n.type === 'pathway') {
      primary = <span className="name"><span className="swatch" style={{ background: '#c084fc' }}></span>{n.label}</span>;
      secondary = `${m.recurring_genes || 0} genes`;
    } else if (n.type === 'trait') {
      primary = <span className="name"><span className="swatch" style={{ background: '#facc15' }}></span>{n.label}</span>;
      secondary = `${m.gene_count || 0} genes · ${formatScore(m.max_score)}`;
    } else if (n.type === 'locus') {
      const axis = AXIS_COLORS[m.primary_axis] || AXIS_COLORS.unknown;
      primary = <span className="name"><span className="swatch" style={{ background: axis }}></span>{n.label}</span>;
      secondary = `${m.n_genes || 0} genes · ${formatScore(m.max_score)}`;
    } else if (n.type === 'evidence') {
      primary = <span className="name"><span className="swatch" style={{ background: '#f472b6' }}></span>{n.label}</span>;
      secondary = `${m.gene_count || 0} genes`;
    } else if (n.type === 'variant') {
      const axis = AXIS_COLORS[m.primary_axis] || AXIS_COLORS.unknown;
      primary = <span className="name"><span className="swatch" style={{ background: axis }}></span>{n.label}</span>;
      secondary = `${formatScore(m.max_score)} · ${m.n_traits || 0}t`;
    }
    return (
      <button key={n.id} className={`GAtlas-top-rail-item ${isSelected ? 'selected' : ''}`} onClick={onClick}>
        {primary}
        <span className="score">{secondary}</span>
      </button>
    );
  };

  const sortOptions = (() => {
    if (kind === 'gene') return [['score', 'score'], ['traits', 'traits'], ['loci', 'loci']];
    if (kind === 'trait') return [['count', 'genes'], ['score', 'max score']];
    return null;
  })();

  return (
    <div className="GAtlas-top-rail">
      <div className="GAtlas-top-rail-header">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={`GAtlas-top-rail-tab ${t.id === tab ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
            title={`${t.count} ${t.label.toLowerCase()}`}
          >
            {t.label}
          </button>
        ))}
        <button
          className="GAtlas-top-rail-min"
          title="Minimize"
          onClick={() => setCollapsed(true)}
        >
          <i className="fas fa-chevron-down"></i>
        </button>
      </div>

      {sortOptions && (
        <div className="GAtlas-top-rail-controls">
          <span>Sort by</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            {sortOptions.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </div>
      )}

      <div className="GAtlas-top-rail-list">
        {items.length === 0 ? (
          <div className="GAtlas-top-rail-empty">Toggle the matching layer above to populate.</div>
        ) : items.map(renderRow)}
      </div>

      <div className="GAtlas-top-rail-footer">
        <div><strong>{formatNumber(totalNodes)}</strong> nodes · <strong>{formatNumber(totalEdges)}</strong> edges</div>
        {selection && (
          <div className="selected-line">
            <span className="swatch" style={{
              background: selection.kind === 'gene' ? (AXIS_COLORS[selection.axis] || AXIS_COLORS.unknown)
                : selection.kind === 'pathway' ? '#c084fc'
                : selection.kind === 'trait' ? '#facc15'
                : selection.kind === 'locus' ? '#fb923c'
                : selection.kind === 'evidence' ? '#f472b6'
                : '#90b4e8'
            }}></span>
            <span>Selected <strong>{selection.id}</strong> · {neighborCount} 1-hop</span>
          </div>
        )}
      </div>
    </div>
  );
};

const ACTION_LABELS = {
  set_layers: (a) => `Toggled layers · ${(a.layers || []).join(', ') || 'none'}`,
  select: (a) => `Selected ${a.kind} · ${a.label}`,
  set_axis_filter: (a) => a.axes?.length ? `Axis filter · ${a.axes.join(', ')}` : 'Cleared axis filter',
  set_evidence_filter: (a) => a.evidence?.length ? `Evidence filter · ${a.evidence.join(', ')}` : 'Cleared evidence filter',
  search: (a) => a.q ? `Searched · "${a.q}"` : 'Cleared search',
  clear_filters: () => 'Cleared all filters',
};

const ACTION_ICONS = {
  set_layers: 'layer-group',
  select: 'crosshairs',
  set_axis_filter: 'palette',
  set_evidence_filter: 'flask',
  search: 'search',
  clear_filters: 'eraser',
};

const SEARCH_TYPES = [
  { id: 'gene', icon: 'network-wired', label: 'Genes' },
  { id: 'locus', icon: 'dna', label: 'Loci' },
  { id: 'variant', icon: 'circle-dot', label: 'Variants' },
  { id: 'pathway', icon: 'project-diagram', label: 'Pathways' },
  { id: 'trait', icon: 'heart-pulse', label: 'Traits' },
];

const GAtlasSearchModal = ({ open, query, onClose, onPick }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTypes, setActiveTypes] = useState(new Set(SEARCH_TYPES.map((t) => t.id)));
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open || !query || query.length < 1) { setResults([]); return undefined; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const url = `${getApiEndpoint('atlasSearch')}?q=${encodeURIComponent(query)}&types=${Array.from(activeTypes).join(',')}&limit=8`;
        const r = await atlasFetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!cancelled) setResults(d.results || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, query, activeTypes]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        const inputEl = document.querySelector('.GAtlas-header-search');
        if (inputEl && inputEl.contains(e.target)) return;
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  const toggleType = (id) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (!open) return null;

  const iconFor = (kind) => SEARCH_TYPES.find((t) => t.id === kind)?.icon || 'search';
  const labelFor = (kind) => SEARCH_TYPES.find((t) => t.id === kind)?.label || kind;

  return (
    <div ref={modalRef} className="GAtlas-search-modal">
      <div className="GAtlas-search-results-container">
        {!query ? (
          <div className="GAtlas-search-empty">Type to search atlas, loci, genes, variants, pathways, traits…</div>
        ) : loading && !results.length ? (
          <div className="GAtlas-search-empty">Searching…</div>
        ) : results.length === 0 ? (
          <div className="GAtlas-search-empty">No results.</div>
        ) : (
          <div className="GAtlas-search-results">
            {results.map((r) => {
              const axisColor = r.axis ? AXIS_COLORS[r.axis] : null;
              return (
                <button
                  key={`${r.kind}-${r.label}-${r.pathway_id || r.gcst_id || ''}`}
                  className="GAtlas-search-item"
                  onClick={() => onPick(r)}
                >
                  <span className="GAtlas-search-item-icon" style={{ '--icon-c': axisColor || NETWORK_COLORS[r.kind] || '#4a6fa5' }}>
                    <i className={`fas fa-${iconFor(r.kind)}`}></i>
                  </span>
                  <span className="GAtlas-search-item-info">
                    <span className="GAtlas-search-item-name">{r.label}</span>
                    <span className="GAtlas-search-item-meta">
                      <span className="GAtlas-search-item-kind">{labelFor(r.kind).slice(0, -1).toLowerCase()}</span>
                      {r.meta && <span> · {r.meta}</span>}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="GAtlas-search-filters">
        {SEARCH_TYPES.map((t) => (
          <label key={t.id} className={`GAtlas-search-filter ${activeTypes.has(t.id) ? 'active' : ''}`} title={t.label}>
            <input type="checkbox" checked={activeTypes.has(t.id)} onChange={() => toggleType(t.id)} />
            <i className={`fas fa-${t.icon}`}></i>
            <span>{t.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

(function applyStoredThemeImmediately() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  let theme = 'dark';
  if (stored === 'light') {
    theme = 'light';
  } else if (stored === 'system') {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    theme = prefersDark ? 'dark' : 'light';
  }
  const root = document.documentElement;
  root.classList.remove('gatlas-theme-light', 'gatlas-theme-dark');
  root.classList.add(theme === 'light' ? 'gatlas-theme-light' : 'gatlas-theme-dark');
})();

function GAtlas() {
  const [selectedItem, setSelectedItem] = useState('home');
  const [theme, setTheme] = useState('dark');
  const [cookieId, setCookieId] = useState('');
  const [daysLeft, setDaysLeft] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [backendStatus, setBackendStatus] = useState('checking');
  const [backendMessage, setBackendMessage] = useState('Checking backend...');
  const [atlasSummary, setAtlasSummary] = useState(null);
  const [atlasRows, setAtlasRows] = useState([]);
  const [candidateRows, setCandidateRows] = useState([]);
  const [mechanismRows, setMechanismRows] = useState([]);
  const [networkData, setNetworkData] = useState({ nodes: [], edges: [], rows: 0 });
  const [atlasLoading, setAtlasLoading] = useState(false);
  const [atlasError, setAtlasError] = useState('');
  const [activeLayers, setActiveLayers] = useState(new Set(['pathway']));
  const [networkFilters, setNetworkFilters] = useState({
    minScore: 0.1,
    maxScore: 0.5,
    minTraits: 3,
    limit: 60, // per-layer hub limit
  });
  const [explorerView, setExplorerView] = useState('network');
  const [explorerAxisFilter, setExplorerAxisFilter] = useState(new Set());
  const [explorerEvidenceFilter, setExplorerEvidenceFilter] = useState(new Set());
  // Pathway filter: { id -> { name, geneSet } }
  const [explorerPathwayFilter, setExplorerPathwayFilter] = useState(new Map());
  const [pathwaySearch, setPathwaySearch] = useState('');
  const [pathwaySuggestions, setPathwaySuggestions] = useState([]);
  const [selection, setSelection] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [topRailTab, setTopRailTab] = useState('genes');
  const [topRailSort, setTopRailSort] = useState('score');
  const [gwasJobs, setGwasJobs] = useState([]);
  const [gwasPipeline, setGwasPipeline] = useState(null);
  const [gwasUploadUnavailable, setGwasUploadUnavailable] = useState('');
  // The sample types the active G-GWAS pipeline accepts, from the catalogue.
  const [gwasSampleCode, setGwasSampleCode] = useState('');
  const [uploadState, setUploadState] = useState({ status: 'idle', percent: 0, fileName: '', error: '' });
  const fileInputRef = useRef(null);
  const [apiOs, setApiOs] = useState('linux');
  const [apiCopied, setApiCopied] = useState(null);
  const [apiTryState, setApiTryState] = useState({});
  const [headerQuery, setHeaderQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [openResultJob, setOpenResultJob] = useState(null);
  const [resultPreview, setResultPreview] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);
  const manhattanCanvasRef = useRef(null);
  const manhattanGeometryRef = useRef({ points: [], scale: null }); // for hit-testing
  const [manhattanHover, setManhattanHover] = useState(null); // { x, y, point }
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentInput, setAgentInput] = useState('');
  const [agentMessages, setAgentMessages] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);

  useEffect(() => {
    ensureGAtlasCookie()
      .then((cookie) => {
        setCookieId(cookie.id);
        setDaysLeft(getCookieDaysLeft());
      })
      .catch((error) => {
        setBackendStatus('offline');
        setBackendMessage(`Credential registration failed: ${error.message}`);
      });

    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) setTheme(stored);
  }, []);

  useEffect(() => {
    if (!cookieId) return;
    loadServiceCatalogue(GWAS_SERVICE_CODE, cookieId)
      .then((catalogue) => {
        const selected = defaultServicePipeline(catalogue);
        setGwasPipeline(selected);
        setGwasSampleCode(selected.sample_codes?.length === 1 ? selected.sample_codes[0] : '');
        if (!selected.sample_codes?.length) {
          setGwasUploadUnavailable('The active G-GWAS pipeline has no XOmics sample-type mapping yet. Existing centralized jobs can be viewed, but new submissions are disabled until that release mapping is added.');
        } else {
          setGwasUploadUnavailable('');
        }
      })
      .catch((error) => setGwasUploadUnavailable(error.message));
  }, [cookieId]);

  useEffect(() => {
    let cancelled = false;

    async function checkBackend() {
      try {
        const resp = await atlasFetch(getApiEndpoint('health'));
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) {
          setBackendStatus('online');
          setBackendMessage(`${data.service || 'G-GWAS'} API online on port ${data.port || '9001'}`);
        }
      } catch (err) {
        if (!cancelled) {
          setBackendStatus('offline');
          setBackendMessage(`Backend check failed: ${err.message}`);
        }
      }
    }

    checkBackend();
    const interval = setInterval(checkBackend, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!getCookieId()) return undefined;

    async function loadAgentHistory() {
      try {
        const resp = await atlasFetch(buildApiUrl('atlasChatHistory', { limit: 80 }));
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const hydrated = (data.rows || []).map((r) => {
          // Synthesize pendingTools from persisted actions so the UI re-renders
          // tool chips on history reload.
          const acts = Array.isArray(r.actions) ? r.actions : [];
          const pendingTools = acts.map((a, i) => ({
            index: i,
            name: a.type === 'select' ? 'select_node' : a.type,
            status: 'done',
          }));
          return { ...r, actions: acts, pendingTools };
        });
        if (!cancelled) setAgentMessages(hydrated);
      } catch (err) {
        if (!cancelled) {
          setAgentMessages([{
            role: 'assistant',
            content: `Agent history is not available yet: ${err.message}`,
            created_at: new Date().toISOString(),
          }]);
        }
      }
    }

    loadAgentHistory();
    return () => {
      cancelled = true;
    };
  }, [cookieId]);

  useEffect(() => {
    let cancelled = false;

    async function loadAtlasSummary() {
      try {
        const resp = await atlasFetch(getApiEndpoint('atlasSummary'));
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setAtlasSummary(data);
      } catch (err) {
        if (!cancelled) setAtlasError(`Atlas summary failed: ${err.message}`);
      }
    }

    loadAtlasSummary();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const q = searchTerm.trim();

    async function loadAtlasTables() {
      setAtlasLoading(true);
      setAtlasError('');
      try {
        const [variantsResp, candidatesResp, mechanismsResp, networkResp] = await Promise.all([
          atlasFetch(buildApiUrl('atlasVariants', {
            discovery: 1,
            min_score: networkFilters.minScore,
            max_score: networkFilters.maxScore,
            min_traits: networkFilters.minTraits,
            limit: selectedItem === 'atlas' ? 120 : 80,
            q,
          })),
          atlasFetch(buildApiUrl('atlasCandidates', {
            limit: selectedItem === 'loci' || selectedItem === 'genes' ? 220 : 120,
            q,
          })),
          atlasFetch(buildApiUrl('atlasMechanisms', {
            limit: selectedItem === 'mechanisms' ? 220 : 120,
            q,
          })),
          atlasFetch(buildApiUrl('atlasNetwork', {
            layers: Array.from(activeLayers).join(','),
            limit: networkFilters.limit,
            q,
          })),
        ]);
        for (const resp of [variantsResp, candidatesResp, mechanismsResp, networkResp]) {
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        }
        const [variants, candidates, mechanisms, network] = await Promise.all([
          variantsResp.json(),
          candidatesResp.json(),
          mechanismsResp.json(),
          networkResp.json(),
        ]);
        if (!cancelled) {
          setAtlasRows(variants.rows || []);
          setCandidateRows(candidates.rows || []);
          setMechanismRows(mechanisms.rows || []);
          setNetworkData(network || { nodes: [], edges: [], rows: 0 });
        }
      } catch (err) {
        if (!cancelled) setAtlasError(`Atlas table load failed: ${err.message}`);
      } finally {
        if (!cancelled) setAtlasLoading(false);
      }
    }

    if (['home', 'atlas', 'mechanisms', 'loci', 'genes'].includes(selectedItem)) {
      loadAtlasTables();
    }

    return () => {
      cancelled = true;
    };
  }, [selectedItem, searchTerm, networkFilters, activeLayers]);

  const handleThemeToggle = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);

    const root = document.documentElement;
    root.classList.remove('gatlas-theme-light', 'gatlas-theme-dark');
    root.classList.add(newTheme === 'light' ? 'gatlas-theme-light' : 'gatlas-theme-dark');
  };

  const renderStatCards = () => {
    const variantCounts = atlasSummary?.variantCounts || {};
    const candidateCounts = atlasSummary?.candidateCounts || {};
    const mainResults = atlasSummary?.mainResults || [];
    const abc = mainResults.find((row) => row.result === 'abc_nasser_same_gene_moderate');
    const coloc = mainResults.find((row) => row.result === 'opentargets_colocalisation_h4_ge_0_8_moderate');
    return (
    <div className="GAtlas-kb-stats-grid">
      <div className="GAtlas-kb-stat-card">
        <div className="GAtlas-kb-stat-icon"><i className="fas fa-dna"></i></div>
        <div className="GAtlas-kb-stat-content">
          <h3>{formatCompact(variantCounts.variants || 1730224)}</h3>
          <p>Scored Atlas Variants</p>
        </div>
      </div>
      <div className="GAtlas-kb-stat-card">
        <div className="GAtlas-kb-stat-icon"><i className="fas fa-layer-group"></i></div>
        <div className="GAtlas-kb-stat-content">
          <h3>1.5k+</h3>
          <p>GWAS Contexts</p>
        </div>
      </div>
      <div className="GAtlas-kb-stat-card">
        <div className="GAtlas-kb-stat-icon"><i className="fas fa-map-marker-alt"></i></div>
        <div className="GAtlas-kb-stat-content">
          <h3>{formatNumber(candidateCounts.candidate_loci || 244)}</h3>
          <p>Prioritized Loci</p>
        </div>
      </div>
      <div className="GAtlas-kb-stat-card">
        <div className="GAtlas-kb-stat-icon"><i className="fas fa-project-diagram"></i></div>
        <div className="GAtlas-kb-stat-content">
          <h3>{abc?.fold ? `${formatScore(abc.fold)}x` : '47.8x'}</h3>
          <p>ABC Support Enrichment</p>
        </div>
      </div>
      <div className="GAtlas-kb-stat-card">
        <div className="GAtlas-kb-stat-icon"><i className="fas fa-link"></i></div>
        <div className="GAtlas-kb-stat-content">
          <h3>{coloc?.fold ? `${formatScore(coloc.fold)}x` : '16.3x'}</h3>
          <p>Colocalisation Enrichment</p>
        </div>
      </div>
      <div className="GAtlas-kb-stat-card">
        <div className="GAtlas-kb-stat-icon"><i className="fas fa-microscope"></i></div>
        <div className="GAtlas-kb-stat-content">
          <h3>{formatNumber(variantCounts.public_functional_variants || 46)}</h3>
          <p>Functional-Support Variants</p>
        </div>
      </div>
    </div>
  )};

  const quickActions = [
    { id: 'atlas', label: 'Open Global Atlas', icon: 'globe', target: 'atlas' },
    { id: 'upload', label: 'Upload GWAS', icon: 'upload', target: 'upload' },
    { id: 'api', label: 'API Workspace', icon: 'code', target: 'api' },
    { id: 'help', label: 'Help & Docs', icon: 'book', target: 'help' },
  ];

  const latestUpdates = [
    {
      id: 1,
      type: 'feature',
      component: 'Conversational Atlas',
      date: '2026-04-28',
      description: 'Ask the atlas in plain language. Answers stream as they are written, and the response can open overlays, focus a node, or apply a filter on your behalf.',
    },
    {
      id: 2,
      type: 'database',
      component: 'Pathway Layer',
      date: '2026-04-28',
      description: 'Candidate genes are now connected through shared biology. Neutrophil degranulation, RHOA GTPase signaling, hypoxia response and mRNA splicing are among the strongest pathway links surfaced so far.',
    },
    {
      id: 3,
      type: 'integration',
      component: 'Bring Your Own GWAS',
      date: '2026-04-28',
      description: 'Upload your own summary statistics and have them scored against the atlas. Your study is overlaid on the same network used everywhere else, so you see exactly where it lands.',
    },
    {
      id: 4,
      type: 'feature',
      component: 'Unified Search',
      date: '2026-04-28',
      description: 'A single bar searches across genes, loci, variants, pathways and traits. Picking a result drops you straight into the atlas with the entity already selected.',
    },
  ];

  const homeResources = [
    { id: 'help', icon: 'question-circle', title: 'Help & Docs', desc: 'Walkthroughs of every page, atlas stats, shortcuts, and how to reach us.', target: 'help', cta: 'Open Help' },
    { id: 'settings', icon: 'cogs', title: 'Settings', desc: 'Theme, workspace identity, and connection status.', target: 'settings', cta: 'Open Settings' },
  ];

  const renderHome = () => (
    <div className="GAtlas-content-full">
      <div className="GAtlas-home-header">
        <div className="GAtlas-welcome-container">
          <div className="GAtlas-welcome-logo-wrapper">
            <img src={gatlasLogo} alt="G-GWAS" className="GAtlas-welcome-logo GAtlas-welcome-logo--dark" />
            <img src={gatlasLogoDark} alt="G-GWAS" className="GAtlas-welcome-logo GAtlas-welcome-logo--light" />
          </div>
          <div className="GAtlas-welcome-text">
            <h1>Welcome to G-GWAS</h1>
            <p>
              Foundation-model GWAS scoring, atlas recurrence, and regulatory mechanism exploration
            </p>
          </div>
        </div>
      </div>

      <div className="GAtlas-home-content">
        <div className="GAtlas-left-column">
          <div className="GAtlas-section GAtlas-kb-stats-section">
            <h2>
              <i className="fas fa-database"></i>
              Atlas Evidence Overview
            </h2>
            {renderStatCards()}
          </div>

          <div className="GAtlas-section GAtlas-quick-actions-section">
            <h2>
              <i className="fas fa-bolt"></i>
              Quick Actions
            </h2>
            <div className="GAtlas-quick-actions-grid">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  className="GAtlas-action-btn"
                  onClick={() => setSelectedItem(action.target)}
                >
                  <div className="GAtlas-action-icon">
                    <i className={`fas fa-${action.icon}`}></i>
                  </div>
                  <div className="GAtlas-action-label">{action.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="GAtlas-right-column">
          <div className="GAtlas-section GAtlas-readiness-section">
            <h2>
              <i className="fas fa-history"></i>
              Latest Updates
            </h2>
            <div className="GAtlas-readiness-list">
              {latestUpdates.map((update) => (
                <div key={update.id} className="GAtlas-readiness-item">
                  <div className="GAtlas-readiness-icon">
                    <i className={`fas fa-${
                      update.type === 'database' ? 'database' :
                      update.type === 'feature' ? 'star' :
                      update.type === 'integration' ? 'plug' : 'sync'
                    }`}></i>
                  </div>
                  <div className="GAtlas-readiness-content">
                    <div className="GAtlas-readiness-header">
                      <h4>{update.component}</h4>
                      <span className="GAtlas-readiness-date">{update.date}</span>
                    </div>
                    <p>{update.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="GAtlas-resources-container">
              <h2>
                <i className="fas fa-tools"></i>
                Additional Resources
              </h2>
              <div className="GAtlas-resources-grid">
                {homeResources.map((r) => (
                  <div key={r.id} className="GAtlas-resource-card">
                    <div className="GAtlas-resource-icon">
                      <i className={`fas fa-${r.icon}`}></i>
                    </div>
                    <div className="GAtlas-resource-content">
                      <h3>{r.title}</h3>
                      <p>{r.desc}</p>
                      <button className="GAtlas-resource-button" onClick={() => setSelectedItem(r.target)}>
                        <i className="fas fa-arrow-right"></i>
                        {r.cta}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDataBanner = () => (
    <div className="GAtlas-data-banner">
      <div>
        <span className={`GAtlas-data-dot ${atlasLoading ? 'loading' : 'ready'}`}></span>
        {atlasLoading ? 'Loading atlas tables' : 'Global atlas tables connected'}
      </div>
      {atlasError && <strong>{atlasError}</strong>}
    </div>
  );

  const renderMetricStrip = () => {
    const variantCounts = atlasSummary?.variantCounts || {};
    const candidateCounts = atlasSummary?.candidateCounts || {};
    return (
      <div className="GAtlas-metric-strip">
        <div><strong>{formatCompact(variantCounts.variants)}</strong><span>variants</span></div>
        <div><strong>{formatCompact(variantCounts.recurrent_variants)}</strong><span>recurrent</span></div>
        <div><strong>{formatCompact(variantCounts.analyzed_region_variants)}</strong><span>atlas region</span></div>
        <div><strong>{formatNumber(candidateCounts.locus_gene_candidates)}</strong><span>locus-gene rows</span></div>
        <div><strong>{formatNumber(candidateCounts.candidate_loci)}</strong><span>loci</span></div>
        <div><strong>{formatNumber(candidateCounts.candidate_genes)}</strong><span>genes</span></div>
      </div>
    );
  };

  const renderVariantTable = (rows = atlasRows) => (
    <div className="GAtlas-table-wrap">
      <table className="GAtlas-data-table">
        <thead>
          <tr>
            <th>Variant</th>
            <th>Locus</th>
            <th>Score</th>
            <th>Traits</th>
            <th>Gene</th>
            <th>Axis</th>
            <th>Evidence</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.snp}-${row.locus_500kb}`}>
              <td>{row.snp}</td>
              <td>{row.locus_500kb}</td>
              <td>{formatScore(row.max_score)}</td>
              <td>{row.n_traits}</td>
              <td>{row.top_gene || '-'}</td>
              <td>{row.primary_axis || '-'}</td>
              <td>
                <span className={`GAtlas-pill ${row.has_candidate ? 'hot' : ''}`}>{row.has_candidate ? 'candidate' : 'atlas'}</span>
                {row.any_external_support ? <span className="GAtlas-pill">external</span> : null}
                {row.any_public_functional_support ? <span className="GAtlas-pill">functional</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderCandidateTable = (rows = candidateRows) => (
    <div className="GAtlas-table-wrap">
      <table className="GAtlas-data-table">
        <thead>
          <tr>
            <th>Locus</th>
            <th>Gene</th>
            <th>Variant</th>
            <th>Score</th>
            <th>Families</th>
            <th>Axis</th>
            <th>Class</th>
            <th>Priority</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.locus_500kb}-${row.gene_symbol}-${row.SNP}`}>
              <td>{row.locus_500kb}</td>
              <td>{row.gene_symbol}</td>
              <td>{row.SNP}</td>
              <td>{formatScore(row.max_score)}</td>
              <td>{row.n_families || '-'}</td>
              <td>{row.primary_axis || '-'}</td>
              <td><span className="GAtlas-pill hot">{row.candidate_class || 'candidate'}</span></td>
              <td>{formatScore(row.priority_score)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderMechanismTable = (rows = mechanismRows) => (
    <div className="GAtlas-table-wrap">
      <table className="GAtlas-data-table">
        <thead>
          <tr>
            <th>Locus</th>
            <th>Gene</th>
            <th>Variant</th>
            <th>Support</th>
            <th>Functional</th>
            <th>Axis</th>
            <th>Mechanism Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.locus_500kb}-${row.gene_symbol}-${row.SNP}-${row.mechanism_priority_score}`}>
              <td>{row.locus_500kb}</td>
              <td>{row.gene_symbol}</td>
              <td>{row.SNP}</td>
              <td><span className="GAtlas-pill hot">{row.mechanism_support_class || row.candidate_class || 'support'}</span></td>
              <td>{row.any_public_functional_support ? 'yes' : 'no'}</td>
              <td>{row.primary_axis || '-'}</td>
              <td>{formatScore(row.mechanism_priority_score)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const updateNetworkFilter = (key, value) => {
    setNetworkFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onNetworkNodeClick = (node) => {
    if (!node) return;
    if (node.type === 'gene') setSelection({ kind: 'gene', id: node.label, axis: axisOf(node), nodeId: node.id });
    else if (node.type === 'locus') setSelection({ kind: 'locus', id: node.label, nodeId: node.id });
    else if (node.type === 'variant') setSelection({ kind: 'variant', id: node.label, nodeId: node.id });
    else if (node.type === 'pathway') setSelection({ kind: 'pathway', id: node.label, nodeId: node.id });
    else if (node.type === 'trait') setSelection({ kind: 'trait', id: node.label, nodeId: node.id, gcst: node.metrics?.gcst_id });
    else if (node.type === 'evidence') setSelection({ kind: 'evidence', id: node.label, nodeId: node.id });
  };

  const handleAxisChipToggle = (axis) => {
    setExplorerAxisFilter((prev) => {
      const next = new Set(prev);
      if (next.has(axis)) next.delete(axis); else next.add(axis);
      return next;
    });
  };

  const handleEvidenceChipToggle = (id) => {
    setExplorerEvidenceFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Close filter popover on outside click
  useEffect(() => {
    if (!filterOpen) return undefined;
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  const totalFilters = explorerAxisFilter.size + explorerEvidenceFilter.size + explorerPathwayFilter.size;
  const clearAllFilters = () => {
    setExplorerAxisFilter(new Set());
    setExplorerEvidenceFilter(new Set());
    setExplorerPathwayFilter(new Map());
  };

  // Debounced pathway search via /pathways/top?q=...
  useEffect(() => {
    if (!filterOpen) return undefined;
    const q = pathwaySearch.trim();
    if (!q) { setPathwaySuggestions([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const url = buildApiUrl('atlasPathwaysTop', { q, limit: 15, min_overlap: 1 });
        const resp = await atlasFetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setPathwaySuggestions(data.rows || []);
      } catch {
        if (!cancelled) setPathwaySuggestions([]);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pathwaySearch, filterOpen]);

  const togglePathway = (row) => {
    setExplorerPathwayFilter((prev) => {
      const next = new Map(prev);
      if (next.has(row.pathway_id)) {
        next.delete(row.pathway_id);
      } else {
        const genes = new Set((row.gene_list || '').split(',').filter(Boolean));
        next.set(row.pathway_id, { name: row.pathway_name, genes });
      }
      return next;
    });
  };

  // Union of all genes across selected pathways (for filtering)
  const pathwayGeneUnion = useMemo(() => {
    if (explorerPathwayFilter.size === 0) return null;
    const u = new Set();
    for (const v of explorerPathwayFilter.values()) {
      for (const g of v.genes) u.add(g);
    }
    return u;
  }, [explorerPathwayFilter]);

  // Sorted top items per node-type for the bottom-right rail
  const railNodesByType = useMemo(() => {
    const buckets = { gene: [], pathway: [], trait: [], locus: [], evidence: [], variant: [] };
    for (const n of (networkData?.nodes || [])) {
      if (buckets[n.type]) buckets[n.type].push(n);
    }
    return buckets;
  }, [networkData]);

  const railSortFor = (kind, sort) => (a, b) => {
    const m = (n, k) => Number(n.metrics?.[k] || 0);
    if (kind === 'gene') {
      if (sort === 'traits') return m(b, 'n_traits') - m(a, 'n_traits');
      if (sort === 'loci') return m(b, 'n_loci') - m(a, 'n_loci');
      const ks = m(b, 'mechanism_priority_score') - m(a, 'mechanism_priority_score');
      if (ks !== 0) return ks;
      return m(b, 'priority_score') - m(a, 'priority_score');
    }
    if (kind === 'pathway') return m(b, 'recurring_genes') - m(a, 'recurring_genes');
    if (kind === 'trait') {
      if (sort === 'score') return m(b, 'max_score') - m(a, 'max_score');
      return m(b, 'gene_count') - m(a, 'gene_count');
    }
    if (kind === 'locus') return m(b, 'n_genes') - m(a, 'n_genes');
    if (kind === 'evidence') return m(b, 'gene_count') - m(a, 'gene_count');
    if (kind === 'variant') return m(b, 'max_score') - m(a, 'max_score');
    return 0;
  };

  const railTabs = useMemo(() => {
    const out = [];
    out.push({ id: 'genes', label: 'Genes', count: railNodesByType.gene.length });
    if (railNodesByType.pathway.length) out.push({ id: 'pathways', label: 'Pathways', count: railNodesByType.pathway.length });
    if (railNodesByType.trait.length) out.push({ id: 'traits', label: 'Traits', count: railNodesByType.trait.length });
    if (railNodesByType.locus.length) out.push({ id: 'loci', label: 'Loci', count: railNodesByType.locus.length });
    if (railNodesByType.evidence.length) out.push({ id: 'evidence', label: 'Evidence', count: railNodesByType.evidence.length });
    if (railNodesByType.variant.length) out.push({ id: 'variants', label: 'Variants', count: railNodesByType.variant.length });
    return out;
  }, [railNodesByType]);

  // If active tab no longer has data, fall back to genes.
  useEffect(() => {
    if (!railTabs.find((t) => t.id === topRailTab)) setTopRailTab('genes');
  }, [railTabs, topRailTab]);

  const renderAtlas = () => {
    const variantCounts = atlasSummary?.variantCounts || {};
    const candidateCounts = atlasSummary?.candidateCounts || {};
    return (
      <div className="GAtlas-data-page">
        {renderPageBanner('globe', 'Global Atlas',
          `${formatCompact(variantCounts.variants || 1730224)} variants · ${formatNumber(candidateCounts.candidate_loci || 244)} loci · ${formatNumber(candidateCounts.candidate_genes || 372)} genes — connected via Reactome biology, GWAS traits, and external evidence.`)}

        <div className="GAtlas-explorer">
          <div className="GAtlas-explorer-toolbar">
            <span className="GAtlas-toolbar-label">Connect via</span>
            {LAYERS.map((l) => {
              const on = activeLayers.has(l.id);
              return (
                <button
                  key={l.id}
                  className={`GAtlas-layer-pill ${on ? 'active' : ''}`}
                  title={l.desc}
                  onClick={() => {
                    setActiveLayers((prev) => {
                      const next = new Set(prev);
                      if (next.has(l.id)) next.delete(l.id); else next.add(l.id);
                      return next;
                    });
                  }}
                >
                  <span className="swatch" style={{ background: l.color }}></span>
                  {l.label}
                </button>
              );
            })}

            <span style={{ flex: 1 }} />

            <div className="GAtlas-explorer-search" ref={filterRef}>
              <i className="fas fa-search"></i>
              <input
                placeholder="Search a gene, rsID, locus…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                className={`GAtlas-filter-trigger inline ${filterOpen ? 'open' : ''}`}
                onClick={() => setFilterOpen((o) => !o)}
                title="Filters"
              >
                <i className="fas fa-filter"></i>
                {totalFilters > 0 && <span className="badge">{totalFilters}</span>}
              </button>

              {filterOpen && (
                <div className="GAtlas-filter-popover">
                  <div className="GAtlas-filter-section">
                    <h5>Trait axis</h5>
                    <div className="GAtlas-filter-section-rows">
                      {AXIS_ORDER.map((a) => (
                        <button
                          key={a}
                          className={`GAtlas-filter-row ${explorerAxisFilter.has(a) ? 'active' : ''}`}
                          onClick={() => handleAxisChipToggle(a)}
                          title={AXIS_LABELS[a]}
                        >
                          <span className="name">
                            <span className="swatch" style={{ background: AXIS_COLORS[a] }}></span>
                            {AXIS_LABELS[a]}
                          </span>
                          <i className="fas fa-check check"></i>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="GAtlas-filter-section">
                    <h5>Evidence</h5>
                    <div className="GAtlas-filter-section-rows">
                      {EVIDENCE_CHIPS.map((c) => (
                        <button
                          key={c.id}
                          className={`GAtlas-filter-row ${explorerEvidenceFilter.has(c.id) ? 'active' : ''}`}
                          onClick={() => handleEvidenceChipToggle(c.id)}
                          title={c.desc}
                        >
                          <span className="name">
                            <span className="swatch" style={{ background: c.color }}></span>
                            {c.label}
                          </span>
                          <i className="fas fa-check check"></i>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="GAtlas-filter-footer">
                    <span className="summary">
                      {totalFilters === 0 ? 'No filters active' : `${totalFilters} filter${totalFilters > 1 ? 's' : ''} active`}
                    </span>
                    <button className="clear" onClick={clearAllFilters} disabled={totalFilters === 0}>Clear all</button>
                  </div>
                </div>
              )}
            </div>

            {Array.from(explorerAxisFilter).map((a) => (
              <span key={`af-${a}`} className="GAtlas-active-filter">
                <span className="swatch" style={{ background: AXIS_COLORS[a] }}></span>
                {AXIS_SHORT[a] || a}
                <button onClick={() => handleAxisChipToggle(a)} aria-label="remove filter">
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
            {Array.from(explorerEvidenceFilter).map((id) => {
              const c = EVIDENCE_CHIPS.find((e) => e.id === id);
              if (!c) return null;
              return (
                <span key={`ef-${id}`} className="GAtlas-active-filter">
                  <span className="swatch" style={{ background: c.color }}></span>
                  {c.label}
                  <button onClick={() => handleEvidenceChipToggle(id)} aria-label="remove filter">
                    <i className="fas fa-times"></i>
                  </button>
                </span>
              );
            })}
            {Array.from(explorerPathwayFilter.entries()).map(([id, v]) => (
              <span key={`pf-${id}`} className="GAtlas-active-filter" title="Reactome pathway">
                <span className="swatch" style={{ background: '#c084fc' }}></span>
                {v.name}
                <button onClick={() => togglePathway({ pathway_id: id, pathway_name: v.name, gene_list: Array.from(v.genes).join(',') })} aria-label="remove pathway filter">
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
          </div>

          <div className="GAtlas-explorer-body">

            <GAtlasForceNetwork
              networkData={networkData}
              loading={atlasLoading}
              axisFilter={explorerAxisFilter}
              evidenceFilter={explorerEvidenceFilter}
              pathwayGeneSet={pathwayGeneUnion}
              searchQuery={searchTerm.trim()}
              selectedId={selection ? (selection.nodeId || `${selection.kind}:${selection.id}`) : null}
              onNodeClick={onNetworkNodeClick}
            />

            {atlasLoading && (
              <div className="GAtlas-canvas-loading">
                <i className="fas fa-circle-notch"></i>
                <span>Loading network…</span>
              </div>
            )}

            <GAtlasTopRail
              tabs={railTabs}
              tab={topRailTab}
              setTab={setTopRailTab}
              sort={topRailSort}
              setSort={setTopRailSort}
              nodesByType={railNodesByType}
              sortFor={railSortFor}
              selection={selection}
              setSelection={setSelection}
              networkData={networkData}
            />


            <GAtlasDetailPanel
              selection={selection}
              networkData={networkData}
              onClose={() => setSelection(null)}
              onNavigate={(s) => setSelection(s)}
            />

            {renderAtlasAgent()}
          </div>
        </div>
      </div>
    );
  };

  const renderMechanisms = () => (
    <div className="GAtlas-data-page">
      {renderPageBanner('layer-group', 'Mechanism-ready loci',
        'Variants → loci → candidate genes ranked by external evidence (L2G, e2G, coloc, eQTL, ABC) and public functional support (MPRA, CRISPR).')}
      {renderEvidenceTable({
        rows: mechanismRows,
        kind: 'mechanism',
        columns: [
          { key: 'gene_symbol', label: 'Gene', primary: true, width: '14ch', click: (r) => ({ kind: 'gene', id: r.gene_symbol }) },
          { key: 'locus_500kb', label: 'Locus', width: '16ch', click: (r) => ({ kind: 'locus', id: r.locus_500kb }) },
          { key: 'SNP', label: 'Variant', mono: true, width: '14ch', click: (r) => ({ kind: 'variant', id: r.SNP }) },
          { key: 'mechanism_priority_score', label: 'Mech score', numeric: true, width: '11ch' },
          { key: 'mechanism_support_class', label: 'Support', width: '20ch' },
          { key: 'any_public_functional_support', label: 'Functional', boolean: true, width: '10ch' },
          { key: 'primary_axis', label: 'Axis', axis: true, width: '17ch' },
        ],
      })}
    </div>
  );

  const renderLoci = () => (
    <div className="GAtlas-data-page">
      {renderPageBanner('dna', 'Prioritized loci',
        '500 kb genomic windows where atlas variants converge on the same candidate gene through multiple evidence layers.')}
      {renderEvidenceTable({
        rows: candidateRows,
        kind: 'locus',
        columns: [
          { key: 'locus_500kb', label: 'Locus', primary: true, width: '16ch', click: (r) => ({ kind: 'locus', id: r.locus_500kb }) },
          { key: 'gene_symbol', label: 'Gene', width: '14ch', click: (r) => ({ kind: 'gene', id: r.gene_symbol }) },
          { key: 'SNP', label: 'Variant', mono: true, width: '14ch', click: (r) => ({ kind: 'variant', id: r.SNP }) },
          { key: 'priority_score', label: 'Priority', numeric: true, width: '10ch' },
          { key: 'max_score', label: 'Max score', numeric: true, width: '10ch' },
          { key: 'n_traits', label: 'Traits', numeric: true, width: '8ch' },
          { key: 'n_families', label: 'Families', numeric: true, width: '8ch' },
          { key: 'primary_axis', label: 'Axis', axis: true, width: '17ch' },
          { key: 'candidate_class', label: 'Class', width: '18ch' },
        ],
      })}
    </div>
  );

  const [evTable, setEvTable] = useState({ colFilters: {}, sortKey: null, sortDir: 'desc' });

  const renderEvidenceTable = ({ rows, kind, columns }) => {
    const cf = evTable.colFilters;
    let filtered = rows || [];
    // Per-column filtering
    const activeFilters = Object.entries(cf).filter(([, v]) => v && v.trim());
    if (activeFilters.length) {
      filtered = filtered.filter((r) => activeFilters.every(([k, v]) => {
        const cell = r[k];
        if (cell == null) return false;
        return String(cell).toLowerCase().includes(v.trim().toLowerCase());
      }));
    }
    if (evTable.sortKey) {
      const k = evTable.sortKey;
      const dir = evTable.sortDir === 'asc' ? 1 : -1;
      filtered = filtered.slice().sort((a, b) => {
        const av = a[k], bv = b[k];
        const an = Number(av), bn = Number(bv);
        if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * dir;
        return String(av || '').localeCompare(String(bv || '')) * dir;
      });
    }

    const navigateTo = (sel) => {
      const nodeId = `${sel.kind}:${sel.id}`;
      setSelection({ kind: sel.kind, id: sel.id, axis: undefined, nodeId });
      setSelectedItem('atlas');
    };

    const setColFilter = (k, v) => setEvTable((prev) => ({ ...prev, colFilters: { ...prev.colFilters, [k]: v } }));
    const toggleSort = (k) => setEvTable((prev) => ({
      ...prev,
      sortKey: k,
      sortDir: prev.sortKey === k && prev.sortDir === 'desc' ? 'asc' : 'desc',
    }));

    return (
      <div className="GAtlas-evidence-page">
        <div className="GAtlas-evidence-tablewrap">
          <table className="GAtlas-data-table GAtlas-evidence-table">
            <colgroup>
              {columns.map((c) => (
                <col key={c.key} style={{ width: c.width || 'auto' }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={c.numeric ? 'numeric' : ''}>
                    <div className="GAtlas-evidence-th">
                      <button className="GAtlas-evidence-thlabel" onClick={() => toggleSort(c.key)}>
                        {c.label}
                        {evTable.sortKey === c.key && (
                          <i className={`fas fa-caret-${evTable.sortDir === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </button>
                      <input
                        className="GAtlas-evidence-thinput"
                        placeholder="filter…"
                        value={cf[c.key] || ''}
                        onChange={(e) => setColFilter(c.key, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 400).map((r, i) => {
                const primary = columns.find((c) => c.primary);
                const primarySel = primary ? primary.click(r) : null;
                return (
                  <tr key={`${r.gene_symbol}-${r.locus_500kb}-${r.SNP}-${i}`}
                      onClick={() => primarySel && navigateTo(primarySel)}
                  >
                    {columns.map((c) => {
                      const v = r[c.key];
                      let cell;
                      if (c.numeric) cell = v == null || v === '' ? '—' : (typeof v === 'number' || (Number.isFinite(Number(v)) && String(v).includes('.'))) ? formatScore(v) : v;
                      else if (c.boolean) cell = v ? '✓' : '';
                      else if (c.axis) cell = v ? (AXIS_LABELS[v] || v) : '—';
                      else if (c.click) {
                        const sel = c.click(r);
                        cell = (
                          <button className="linklike" onClick={(e) => { e.stopPropagation(); navigateTo(sel); }}>
                            {v || '—'}
                          </button>
                        );
                      }
                      else cell = v == null || v === '' ? '—' : v;
                      const cls = [
                        c.mono ? 'mono' : '',
                        c.primary ? 'primary' : '',
                        c.numeric ? 'numeric' : '',
                        c.boolean ? 'boolean' : '',
                      ].filter(Boolean).join(' ');
                      return (
                        <td key={c.key} className={cls}>{cell}</td>
                      );
                    })}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={columns.length} className="GAtlas-evidence-empty">No rows match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const refreshGwasJobs = useCallback(async () => {
    if (!cookieId) return;
    try {
      const data = await loadServiceJobs(GWAS_SERVICE_CODE, cookieId, { limit: 50 });
      setGwasJobs(data.jobs.map((job) => ({
        ...job,
        id: job.job_uuid || job.upload_uuid,
        name: job.sample_name,
        size_bytes: Number(job.rawUpload?.total_bytes || 0),
        status: job.status === 'completed'
          ? 'done' : job.status === 'failed'
            ? 'error' : job.status,
      })));
    } catch (err) {
      setGwasUploadUnavailable((current) => current || err.message);
    }
  }, [cookieId]);

  useEffect(() => {
    if (selectedItem !== 'upload') return undefined;
    refreshGwasJobs();
    const interval = setInterval(refreshGwasJobs, 4000);
    return () => clearInterval(interval);
  }, [selectedItem, refreshGwasJobs]);

  // Auto-select the first done job when entering the page (or when jobs first load)
  useEffect(() => {
    if (selectedItem !== 'upload') return;
    if (openResultJob) return;
    const firstDone = (gwasJobs || []).find((j) => j.status === 'done');
    if (firstDone) setOpenResultJob(firstDone);
  }, [selectedItem, gwasJobs, openResultJob]);

  const handleGwasUpload = async (file) => {
    if (!file || !cookieId || !gwasPipeline || gwasUploadUnavailable || !gwasSampleCode) return;
    setUploadState({ status: 'uploading', percent: 0, fileName: file.name, error: '' });
    try {
      await createServiceUpload({
        serviceCode: GWAS_SERVICE_CODE,
        token: cookieId,
        pipeline: gwasPipeline,
        sampleCode: gwasSampleCode,
        selectedFiles: [{ file, slotPosition: 1, label: 'GWAS summary statistics' }],
        displayName: file.name,
        metadata: { original_filename: file.name },
        onProgress: ({ fraction }) => {
          setUploadState((previous) => ({ ...previous, percent: Math.round(fraction * 100) }));
        },
      });
      setUploadState({ status: 'queued', percent: 100, fileName: file.name, error: '' });
      await refreshGwasJobs();
      setTimeout(() => setUploadState({ status: 'idle', percent: 0, fileName: '', error: '' }), 2000);
    } catch (err) {
      setUploadState({ status: 'error', percent: 0, fileName: file.name, error: err.message });
    }
  };

  // The centralized contract exposes declared outputs and the complete result
  // bundle. The old server-specific preview payload is intentionally not
  // recreated here.
  useEffect(() => {
    if (!openResultJob) { setResultPreview(null); return undefined; }
    setResultLoading(false);
    setResultPreview({
      error: 'Inline result preview is not part of the centralized G-GWAS contract. Download the completed result bundle.',
    });
    return undefined;
  }, [openResultJob, cookieId]);

  const drawManhattan = useCallback((highlightIdx = -1) => {
    const canvas = manhattanCanvasRef.current;
    if (!canvas || !resultPreview || resultPreview.error) return;
    const points = resultPreview.manhattan || [];
    const ctx = canvas.getContext('2d');
    const cssWidth = canvas.clientWidth || 800;
    const cssHeight = canvas.clientHeight || 220;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== cssWidth * dpr || canvas.height !== cssHeight * dpr) {
      canvas.width = cssWidth * dpr;
      canvas.height = cssHeight * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const maxScore = Math.max(0.1, resultPreview.max_score || 0.5);
    const padL = 36, padR = 12, padT = 12, padB = 28;
    const innerW = cssWidth - padL - padR;
    const innerH = cssHeight - padT - padB;

    const chromOrder = ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18','19','20','21','22','X','Y'];
    const cw = innerW / chromOrder.length;
    const chromIdx = Object.fromEntries(chromOrder.map((c, i) => [c, i]));
    const chromColors = ['#5eead4', '#fb923c', '#90b4e8', '#f472b6', '#facc15', '#c084fc'];

    chromOrder.forEach((c, i) => {
      ctx.fillStyle = i % 2 === 0 ? '#1a1a1a' : '#1c1c1c';
      ctx.fillRect(padL + i * cw, padT, cw, innerH);
    });

    // Y-axis grid + ticks
    ctx.lineWidth = 1;
    ctx.font = '10px Inter, sans-serif';
    for (let i = 0; i <= 4; i++) {
      const v = (maxScore / 4) * i;
      const y = padT + innerH - (v / maxScore) * innerH;
      ctx.beginPath();
      ctx.moveTo(padL, y); ctx.lineTo(cssWidth - padR, y);
      ctx.strokeStyle = i === 0 ? '#333' : '#222';
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.fillText(v.toFixed(2), 4, y + 3);
    }

    chromOrder.forEach((c, i) => {
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillText(c, padL + i * cw + cw / 2 - 4, cssHeight - 10);
    });

    const maxBpByChrom = {};
    for (const p of points) {
      maxBpByChrom[p.chr] = Math.max(maxBpByChrom[p.chr] || 0, p.bp);
    }

    // Compute screen-space positions and store for hit-testing
    const placed = new Array(points.length);
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const ci = chromIdx[String(p.chr)];
      if (ci === undefined) { placed[i] = null; continue; }
      const maxBp = maxBpByChrom[p.chr] || 1;
      const x = padL + ci * cw + (p.bp / maxBp) * cw;
      const y = padT + innerH - (p.score / maxScore) * innerH;
      placed[i] = { x, y, ci };
    }
    manhattanGeometryRef.current = { placed, padL, padT, innerW, innerH, cssWidth, cssHeight };

    // Draw points
    for (let i = 0; i < points.length; i++) {
      const sp = placed[i];
      if (!sp) continue;
      const isHover = i === highlightIdx;
      ctx.fillStyle = chromColors[sp.ci % chromColors.length];
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, isHover ? 3.5 : 1.6, 0, Math.PI * 2);
      ctx.fill();
      if (isHover) {
        ctx.lineWidth = 1.6;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
      }
    }
  }, [resultPreview]);

  // Draw whenever data changes, AND on every page entry to /upload
  useEffect(() => {
    if (selectedItem !== 'upload') return;
    if (!manhattanCanvasRef.current) return;
    // Defer one frame so layout is committed (clientWidth/Height non-zero)
    const id = requestAnimationFrame(() => drawManhattan(-1));
    setManhattanHover(null);
    return () => cancelAnimationFrame(id);
  }, [drawManhattan, selectedItem, openResultJob]);

  // Ref-callback: draws as soon as the canvas mounts (fixes the
  // "navigate away then back, chart blank" case)
  const setManhattanCanvas = useCallback((el) => {
    manhattanCanvasRef.current = el;
    if (el && resultPreview && !resultPreview.error) {
      requestAnimationFrame(() => drawManhattan(-1));
    }
  }, [drawManhattan, resultPreview]);

  // Redraw on window resize so the chart adapts to layout changes
  useEffect(() => {
    if (!manhattanCanvasRef.current) return undefined;
    const handler = () => drawManhattan(-1);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [drawManhattan]);

  // Hit-test on mouse move; show tooltip when within ~5 px of a point.
  const onManhattanMouseMove = useCallback((e) => {
    if (!manhattanGeometryRef.current.placed) return;
    const canvas = manhattanCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const placed = manhattanGeometryRef.current.placed;
    const points = resultPreview?.manhattan || [];
    let bestIdx = -1; let bestD2 = 36; // 6px radius
    for (let i = 0; i < placed.length; i++) {
      const sp = placed[i];
      if (!sp) continue;
      const dx = sp.x - mx, dy = sp.y - my;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) { bestD2 = d2; bestIdx = i; }
    }
    if (bestIdx < 0) {
      if (manhattanHover) { setManhattanHover(null); drawManhattan(-1); }
      return;
    }
    const sp = placed[bestIdx];
    const point = points[bestIdx];
    setManhattanHover({ x: sp.x, y: sp.y, point });
    drawManhattan(bestIdx);
  }, [drawManhattan, manhattanHover, resultPreview]);

  const onManhattanMouseLeave = useCallback(() => {
    if (manhattanHover) { setManhattanHover(null); drawManhattan(-1); }
  }, [manhattanHover, drawManhattan]);

  const downloadGwasResult = async (job) => {
    try {
      await openJobDownload(GWAS_SERVICE_CODE, cookieId, job);
    } catch (error) {
      setUploadState((previous) => ({ ...previous, status: 'error', error: error.message }));
    }
  };

  const renderMyGwas = () => {
    const formatBytes = (n) => {
      if (!n) return '-';
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      if (n < 1024 * 1024 * 1024) return `${(n / 1048576).toFixed(1)} MB`;
      return `${(n / 1073741824).toFixed(2)} GB`;
    };
    const relative = (iso) => {
      if (!iso) return '-';
      const t = new Date(iso).getTime();
      const dt = Math.max(0, Date.now() - t) / 1000;
      if (dt < 60) return `${Math.floor(dt)}s ago`;
      if (dt < 3600) return `${Math.floor(dt / 60)}m ago`;
      if (dt < 86400) return `${Math.floor(dt / 3600)}h ago`;
      return `${Math.floor(dt / 86400)}d ago`;
    };

    const us = uploadState;
    const uploadingMsg = {
      idle: '',
      signing: 'Requesting signed URL…',
      uploading: `Uploading ${us.fileName} — ${us.percent}%`,
      submitting: 'Queueing job…',
      queued: 'Queued ✓',
      error: us.error,
    }[us.status] || '';

    return (
      <div className="GAtlas-data-page">
        {renderPageBanner('upload', 'My GWAS',
          'Upload summary statistics through XOmics and download the completed G-GWAS result bundle.')}

        <div className="GAtlas-mygwas">
          <div className="GAtlas-mygwas-left">
            <section className="GAtlas-mygwas-uploader">
              <h3>Upload GWAS sumstats</h3>
              <p className="muted">
                TSV or TSV.gz, harmonized or standard EBI columns. Processing uses the active centralized G-GWAS pipeline.
              </p>
              {gwasUploadUnavailable && (
                <div className="GAtlas-mygwas-empty">{gwasUploadUnavailable}</div>
              )}
              {(gwasPipeline?.sample_codes?.length || 0) > 1 && (
                <div className="GAtlas-mygwas-sample-type">
                  <label htmlFor="ggwas-sample-type">Sample type</label>
                  <select
                    id="ggwas-sample-type"
                    value={gwasSampleCode}
                    onChange={(event) => setGwasSampleCode(event.target.value)}
                    disabled={us.status === 'uploading'}
                  >
                    <option value="">Select...</option>
                    {gwasPipeline.sample_codes.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                </div>
              )}
              <div
                className={`GAtlas-mygwas-drop ${(us.status !== 'idle' && us.status !== 'queued') || gwasUploadUnavailable || !gwasSampleCode ? 'busy' : ''}`}
                onClick={() => !gwasUploadUnavailable && gwasSampleCode && fileInputRef.current && fileInputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files && e.dataTransfer.files[0];
                  if (f && !gwasUploadUnavailable && gwasSampleCode) handleGwasUpload(f);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".tsv,.tsv.gz,.gz,.txt"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f && !gwasUploadUnavailable) handleGwasUpload(f);
                    e.target.value = '';
                  }}
                />
                <i className="fas fa-cloud-upload-alt"></i>
                <strong>Drop a GWAS sumstat file here, or click to pick one</strong>
                <span className="muted">SNP, CHR, BP, P, BETA (or harmonized hm_*)</span>
                {us.status !== 'idle' && (
                  <div className={`GAtlas-mygwas-progress ${us.status}`}>
                    <div className="bar" style={{ width: `${us.percent}%` }} />
                    <span>{uploadingMsg}</span>
                  </div>
                )}
              </div>
            </section>

            <WebserverDemoSection serviceCode={GWAS_SERVICE_CODE} />

            <section className="GAtlas-mygwas-jobs">
              <div className="GAtlas-mygwas-jobs-header">
                <h3>Jobs</h3>
              </div>

              {gwasJobs.length === 0 ? (
                <div className="GAtlas-mygwas-empty">No jobs yet. Upload a file above to start.</div>
              ) : (
                <table className="GAtlas-data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Size</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {gwasJobs.map((j) => {
                      const isSel = openResultJob && openResultJob.id === j.id;
                      const onSelect = () => {
                        if (j.status === 'done') setOpenResultJob(j);
                      };
                      return (
                        <tr
                          key={j.id}
                          className={isSel ? 'selected' : ''}
                          onClick={onSelect}
                        >
                          <td>
                            {j.name}
                            {Number(j.is_demo) === 1 && <span className="GAtlas-mygwas-demo-tag">demo</span>}
                          </td>
                          <td>{formatBytes(j.size_bytes)}</td>
                          <td><span className={`GAtlas-mygwas-status ${j.status}`}>{j.status}</span></td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {j.status === 'done' && (
                              <button className="GAtlas-link-btn" onClick={() => downloadGwasResult(j)} title="Download result bundle">
                                <i className="fas fa-download"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          <div className="GAtlas-mygwas-right">
            {!openResultJob ? (
              <div className="GAtlas-result-pane-empty">
                Select a finished job on the left to see its results.
              </div>
            ) : (
              <>
                <div className="GAtlas-result-pane-header">
                  <div>
                    <h3>{openResultJob.name}</h3>
                    <span className="muted">job #{openResultJob.id}{resultPreview && resultPreview.total_variants ? ` · ${formatNumber(resultPreview.total_variants)} variants` : ''}</span>
                  </div>
                  <button className="GAtlas-detail-close" onClick={() => setOpenResultJob(null)} title="Close">
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {resultLoading && <div className="GAtlas-result-loading">Computing preview…</div>}
                {resultPreview && resultPreview.error && (
                  <div className="GAtlas-result-loading" style={{ color: '#fca5a5' }}>{resultPreview.error}</div>
                )}

                {resultPreview && !resultPreview.error && (
                  <div className="GAtlas-result-pane">
                    <section>
                      <h5>Score landscape <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '1vh', textTransform: 'none', letterSpacing: 0 }}>(hover a point to inspect)</span></h5>
                      <div className="GAtlas-manhattan-wrap">
                        <canvas
                          ref={setManhattanCanvas}
                          className="GAtlas-manhattan-canvas"
                          onMouseMove={onManhattanMouseMove}
                          onMouseLeave={onManhattanMouseLeave}
                          onClick={() => {
                            if (!manhattanHover) return;
                            const p = manhattanHover.point;
                            setSelection({ kind: 'variant', id: p.snp, nodeId: `variant:${p.snp}` });
                            setSelectedItem('atlas');
                          }}
                        />
                        {manhattanHover && (
                          <div
                            className="GAtlas-manhattan-tooltip"
                            style={{
                              left: Math.min(Math.max(manhattanHover.x + 10, 0), (manhattanCanvasRef.current?.clientWidth || 800) - 160),
                              top: Math.max(manhattanHover.y - 50, 0),
                            }}
                          >
                            <strong>{manhattanHover.point.snp}</strong>
                            <span>chr{manhattanHover.point.chr}:{Number(manhattanHover.point.bp).toLocaleString()}</span>
                            <span>score <b>{formatScore(manhattanHover.point.score)}</b></span>
                            <em>click → open in atlas</em>
                          </div>
                        )}
                      </div>
                      <div className="GAtlas-result-stats">
                        <div><strong>{formatNumber(resultPreview.total_variants)}</strong><span>variants scored</span></div>
                        <div><strong>{formatScore(resultPreview.max_score)}</strong><span>max score</span></div>
                        <div><strong>{formatNumber((resultPreview.score_histogram || []).slice(1).reduce((a, b) => a + b, 0))}</strong><span>score ≥ 0.1</span></div>
                        <div><strong>{(resultPreview.chrom_summary || []).length}</strong><span>chromosomes</span></div>
                      </div>
                    </section>

                    <section>
                      <h5>Atlas overlap</h5>
                      {(() => {
                        const c = (resultPreview.atlas && resultPreview.atlas.counts) || {};
                        return (
                          <div className="GAtlas-atlas-overlap">
                            <div className="overlap-tile">
                              <strong>{formatNumber(c.user_variants_in_atlas || 0)}</strong>
                              <span>your high-score variants in atlas</span>
                            </div>
                            <div className="overlap-tile">
                              <strong>{c.candidate_genes_hit || 0}</strong>
                              <span>candidate genes hit</span>
                            </div>
                            <div className="overlap-tile">
                              <strong>{c.loci_hit || 0}</strong>
                              <span>loci hit</span>
                            </div>
                            <div className="overlap-tile">
                              <strong>{c.pathways_hit || 0}</strong>
                              <span>pathways hit</span>
                            </div>
                            <div className="overlap-tile">
                              <strong>{c.traits_hit || 0}</strong>
                              <span>traits hit</span>
                            </div>
                          </div>
                        );
                      })()}

                      {(resultPreview.atlas?.genes || []).length > 0 && (
                        <>
                          <h6>Top atlas genes hit</h6>
                          <table>
                            <thead><tr><th>Gene</th><th>Axis</th><th>Score</th></tr></thead>
                            <tbody>
                              {resultPreview.atlas.genes.slice(0, 10).map((g) => (
                                <tr key={g.gene_symbol}>
                                  <td>
                                    <button className="linklike" onClick={() => {
                                      setSelection({ kind: 'gene', id: g.gene_symbol, axis: g.primary_axis, nodeId: `gene:${g.gene_symbol}` });
                                      setSelectedItem('atlas');
                                    }}>{g.gene_symbol}</button>
                                  </td>
                                  <td>{g.primary_axis ? (AXIS_SHORT[g.primary_axis] || g.primary_axis) : '-'}</td>
                                  <td>{formatScore(g.priority_score || g.max_score)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      {(resultPreview.atlas?.pathways || []).length > 0 && (
                        <>
                          <h6>Top pathways your variants converge on</h6>
                          <table>
                            <thead><tr><th>Pathway</th><th>Genes</th></tr></thead>
                            <tbody>
                              {resultPreview.atlas.pathways.slice(0, 8).map((p) => (
                                <tr key={p.pathway_id}>
                                  <td>{p.pathway_name}</td>
                                  <td>{p.hits}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      {(resultPreview.atlas?.traits || []).length > 0 && (
                        <>
                          <h6>Top GWAS traits your variants share</h6>
                          <table>
                            <thead><tr><th>Trait</th><th>Variants</th></tr></thead>
                            <tbody>
                              {resultPreview.atlas.traits.slice(0, 8).map((t) => (
                                <tr key={t.gcst_id}>
                                  <td>{t.trait || t.mapped_trait || t.gcst_id}</td>
                                  <td>{t.variants_hit}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </section>

                    <section>
                      <h5>Top variants</h5>
                      <table>
                        <thead><tr><th>SNP</th><th>Locus</th><th>Score</th></tr></thead>
                        <tbody>
                          {(resultPreview.top_variants || []).slice(0, 15).map((v) => (
                            <tr key={v.snp}>
                              <td>{v.snp}</td>
                              <td>chr{v.chr}:{Number(v.bp).toLocaleString()}</td>
                              <td>{formatScore(v.score)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGenes = () => (
    <div className="GAtlas-data-page">
      {renderPageBanner('network-wired', 'Candidate genes',
        '372 genes prioritized by recurrence across the GWAS atlas, ranked by mechanism score and trait pleiotropy.')}
      {renderEvidenceTable({
        rows: candidateRows,
        kind: 'gene',
        columns: [
          { key: 'gene_symbol', label: 'Gene', primary: true, width: '14ch', click: (r) => ({ kind: 'gene', id: r.gene_symbol }) },
          { key: 'locus_500kb', label: 'Locus', width: '16ch', click: (r) => ({ kind: 'locus', id: r.locus_500kb }) },
          { key: 'SNP', label: 'Top variant', mono: true, width: '14ch', click: (r) => ({ kind: 'variant', id: r.SNP }) },
          { key: 'priority_score', label: 'Priority', numeric: true, width: '10ch' },
          { key: 'max_score', label: 'Max score', numeric: true, width: '10ch' },
          { key: 'n_traits', label: 'Traits', numeric: true, width: '8ch' },
          { key: 'primary_axis', label: 'Axis', axis: true, width: '17ch' },
          { key: 'candidate_class', label: 'Class', width: '18ch' },
        ],
      })}
    </div>
  );

  const [pendingSelect, setPendingSelect] = useState(null);

  // Dispatch actions returned by the agent.
  const applyAgentActions = (actions) => {
    if (!Array.isArray(actions)) return;
    let newSelection = null;
    for (const a of actions) {
      if (!a || typeof a !== 'object') continue;
      switch (a.type) {
        case 'set_layers':
          if (Array.isArray(a.layers)) setActiveLayers(new Set(a.layers.filter(Boolean)));
          break;
        case 'set_axis_filter':
          if (Array.isArray(a.axes)) setExplorerAxisFilter(new Set(a.axes));
          break;
        case 'set_evidence_filter':
          if (Array.isArray(a.evidence)) setExplorerEvidenceFilter(new Set(a.evidence));
          break;
        case 'clear_filters':
          setExplorerAxisFilter(new Set());
          setExplorerEvidenceFilter(new Set());
          setExplorerPathwayFilter(new Map());
          break;
        case 'search':
          setSearchTerm(typeof a.q === 'string' ? a.q : '');
          break;
        case 'set_tab':
          if (a.tab) setTopRailTab(a.tab);
          break;
        case 'select':
          if (a.kind && a.label) newSelection = { kind: a.kind, label: a.label };
          break;
        default:
          break;
      }
    }
    if (newSelection) {
      // Try to resolve nodeId immediately from current networkData; if not found,
      // queue it — the next networkData refresh effect will apply it.
      const node = (networkData?.nodes || []).find(
        (n) => n.type === newSelection.kind && n.label === newSelection.label
      );
      if (node) {
        setSelection({ kind: node.type, id: node.label, axis: node.metrics?.primary_axis, nodeId: node.id });
      } else {
        setPendingSelect(newSelection);
      }
    }
    if (actions.some((a) => a && (a.type === 'set_layers' || a.type === 'select'))) {
      setSelectedItem('atlas');
    }
  };

  // Resolve a pending select once networkData updates with the right node.
  useEffect(() => {
    if (!pendingSelect) return;
    const node = (networkData?.nodes || []).find(
      (n) => n.type === pendingSelect.kind && n.label === pendingSelect.label
    );
    if (node) {
      setSelection({ kind: node.type, id: node.label, axis: node.metrics?.primary_axis, nodeId: node.id });
      setPendingSelect(null);
    }
  }, [networkData, pendingSelect]);

  // Whenever a selection is set (from anywhere — network click, agent, table row,
  // detail panel cross-link), auto-enable the matching layer so the node is actually
  // present in the rendered graph.
  useEffect(() => {
    if (!selection?.kind) return;
    const layerForKind = {
      pathway: 'pathway',
      trait: 'trait',
      locus: 'locus',
      evidence: 'evidence',
      variant: 'variant',
    };
    const need = layerForKind[selection.kind];
    if (!need) return;
    setActiveLayers((prev) => (prev.has(need) ? prev : new Set([...prev, need])));
  }, [selection]);

  const sendAgentMessage = async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : agentInput).trim();
    if (!text || agentLoading) return;
    const startTs = new Date().toISOString();
    setAgentMessages((prev) => [
      ...prev,
      { role: 'user', content: text, created_at: startTs },
      // Empty assistant placeholder we'll fill incrementally
      { role: 'assistant', content: '', actions: [], pendingTools: [], streaming: true, created_at: startTs },
    ]);
    setAgentInput('');
    setAgentLoading(true);

    // Helper to update the *last* message (the assistant placeholder)
    const updateLast = (mut) => setAgentMessages((prev) => {
      const next = prev.slice();
      const i = next.length - 1;
      if (i < 0 || next[i].role !== 'assistant') return prev;
      next[i] = mut(next[i]);
      return next;
    });

    try {
      const resp = await atlasFetch(getApiEndpoint('atlasChatMessage'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text,
          context: {
            selectedItem,
            searchTerm,
            filters: networkFilters,
            active_layers: Array.from(activeLayers),
            active_axes: Array.from(explorerAxisFilter),
            active_evidence: Array.from(explorerEvidenceFilter),
            selected: selection ? { kind: selection.kind, label: selection.id } : null,
            networkRows: networkData?.rows || 0,
          },
        }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const ev = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const m = ev.match(/^data: (.*)$/m);
          if (!m) continue;
          let parsed;
          try { parsed = JSON.parse(m[1]); } catch { continue; }
          if (parsed.type === 'text' && typeof parsed.delta === 'string') {
            updateLast((msg) => ({ ...msg, content: (msg.content || '') + parsed.delta }));
          } else if (parsed.type === 'tool_call_start') {
            updateLast((msg) => ({
              ...msg,
              pendingTools: [...(msg.pendingTools || []), { index: parsed.index, name: parsed.name, status: 'running' }],
            }));
          } else if (parsed.type === 'action') {
            // Mark the corresponding tool call done + run it on the UI
            updateLast((msg) => ({
              ...msg,
              pendingTools: (msg.pendingTools || []).map((t) => t.index === parsed.index ? { ...t, status: 'done' } : t),
              actions: [...(msg.actions || []), parsed.action],
            }));
            applyAgentActions([parsed.action]);
          } else if (parsed.type === 'error') {
            updateLast((msg) => ({ ...msg, content: (msg.content || '') + `\n\n[Error: ${parsed.message}]` }));
          } else if (parsed.type === 'done') {
            updateLast((msg) => ({ ...msg, streaming: false, model: parsed.model }));
          }
        }
      }
    } catch (err) {
      updateLast((msg) => ({
        ...msg,
        streaming: false,
        content: (msg.content || '') + `\n[Agent request failed: ${err.message}]`,
      }));
    } finally {
      setAgentLoading(false);
    }
  };

  const renderAtlasAgent = () => (
    <div className={`GAtlas-agent ${agentOpen ? 'open' : ''}`}>
      {!agentOpen && (
        <button className="GAtlas-agent-fab" onClick={() => setAgentOpen(true)}>
          <i className="fas fa-comments"></i>
          <span>Atlas Agent</span>
        </button>
      )}
      {agentOpen && (
        <div className="GAtlas-agent-panel">
          <div className="GAtlas-agent-header">
            <div>
              <strong>Atlas Agent</strong>
              <span>Ask about variants, loci, genes, evidence, or filters</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5vh' }}>
              <button
                title="Clear chat history"
                onClick={async () => {
                  try {
                    await atlasFetch(getApiEndpoint('atlasChatClear'), {
                      method: 'DELETE',
                    });
                  } catch {}
                  setAgentMessages([]);
                }}
              >
                <i className="fas fa-trash"></i>
              </button>
              <button onClick={() => setAgentOpen(false)}><i className="fas fa-times"></i></button>
            </div>
          </div>
          <div className="GAtlas-agent-history">
            {agentMessages.length === 0 && (
              <>
                <div className="GAtlas-agent-empty">
                  The agent can drive the network for you — toggling layers, applying filters, and selecting nodes.
                </div>
                <div className="GAtlas-agent-suggestions">
                  {[
                    { tag: 'Pathway', q: 'show me NOTCH-related pathways' },
                    { tag: 'Gene', q: 'open NFE2L2 and show its trait hubs' },
                    { tag: 'Filter', q: 'filter to neural-enriched genes with functional support' },
                    { tag: 'Trait', q: 'which genes recur across cardiovascular GWAS' },
                  ].map((s) => (
                    <button
                      key={s.q}
                      className="GAtlas-agent-suggestion"
                      onClick={() => sendAgentMessage(s.q)}
                    >
                      <strong>{s.tag}</strong>
                      {s.q}
                    </button>
                  ))}
                </div>
              </>
            )}
            {agentMessages.map((msg, index) => {
              const tools = msg.pendingTools || [];
              return (
                <div key={`${msg.created_at || index}-${index}`} className={`GAtlas-agent-message ${msg.role}`}>
                  <span>{msg.role === 'user' ? 'You' : 'G-GWAS'}</span>
                  {msg.content && (
                    <div className="GAtlas-agent-md">
                      {msg.role === 'user' ? (
                        <p>{msg.content}</p>
                      ) : (
                        <ReactMarkdown
                          components={{
                            // Render single-line paragraphs without extra margin
                            p: ({ node, ...props }) => <p {...props} />,
                            // Style links to open in a new tab
                            a: ({ node, ...props }) => <a target="_blank" rel="noreferrer" {...props} />,
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      )}
                      {msg.streaming && <span className="GAtlas-agent-cursor"></span>}
                    </div>
                  )}
                  {!msg.content && msg.streaming && tools.length === 0 && (
                    <p className="GAtlas-agent-thinking">thinking…</p>
                  )}
                  {tools.length > 0 && (
                    <div className="GAtlas-agent-tools">
                      {tools.map((t, ti) => {
                        // Find the corresponding action (if executed) for prettier label
                        const matchingAction = (msg.actions || [])[ti];
                        const actionType = matchingAction ? matchingAction.type : (
                          t.name === 'select_node' ? 'select' :
                          t.name === 'set_layers' ? 'set_layers' :
                          t.name === 'set_axis_filter' ? 'set_axis_filter' :
                          t.name === 'set_evidence_filter' ? 'set_evidence_filter' :
                          t.name === 'search' ? 'search' :
                          t.name === 'clear_filters' ? 'clear_filters' : t.name
                        );
                        const label = matchingAction
                          ? (ACTION_LABELS[actionType] ? ACTION_LABELS[actionType](matchingAction) : actionType)
                          : `Calling ${t.name}…`;
                        const icon = ACTION_ICONS[actionType] || 'cog';
                        return (
                          <div key={ti} className={`GAtlas-agent-tool ${t.status}`}>
                            <i className={`fas fa-${icon}`}></i>
                            <span>{label}</span>
                            {t.status === 'running' && <i className="fas fa-circle-notch GAtlas-agent-spin"></i>}
                            {t.status === 'done' && <i className="fas fa-check"></i>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="GAtlas-agent-input">
            <input
              value={agentInput}
              onChange={(e) => setAgentInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendAgentMessage();
                }
              }}
              placeholder="Ask the atlas agent..."
            />
            <button onClick={sendAgentMessage} disabled={agentLoading || !agentInput.trim()}>
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderPageBanner = (icon, title, description) => (
    <div className="GAtlas-page-banner">
      <div className="GAtlas-page-banner-icon">
        <i className={`fas fa-${icon}`}></i>
      </div>
      <div className="GAtlas-page-banner-info">
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </div>
  );

  const renderPlaceholder = (icon, title, text) => (
    <div className="GAtlas-content-full">
      {renderPageBanner(icon, title, text)}
      <div className="GAtlas-content-empty">
        <div className="GAtlas-empty-icon"><i className={`fas fa-${icon}`}></i></div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="GAtlas-data-page">
      {renderPageBanner('cog', 'Settings', 'Workspace preferences and display.')}

      <div className="GAtlas-settings">
        <section className="GAtlas-settings-card">
          <h3>Appearance</h3>
          <div className="GAtlas-settings-row">
            <div className="GAtlas-settings-row-label">
              <strong>Theme</strong>
              <span>Switch between the dark navy palette and a soft white-on-grey light theme.</span>
            </div>
            <div className="GAtlas-settings-theme-options">
              <button
                className={`GAtlas-theme-option ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => handleThemeToggle('dark')}
              >
                <i className="fas fa-moon"></i> Dark
              </button>
              <button
                className={`GAtlas-theme-option ${theme === 'light' ? 'active' : ''}`}
                onClick={() => handleThemeToggle('light')}
              >
                <i className="fas fa-sun"></i> Light
              </button>
            </div>
          </div>
        </section>

        <section className="GAtlas-settings-card">
          <h3>Workspace identity</h3>
          <div className="GAtlas-settings-row">
            <div className="GAtlas-settings-row-label">
              <strong>Cookie ID</strong>
              <span>Auto-issued on first visit. Used as your API key, your job ownership token, and your chat scope. Resets if cleared from your browser; expires in {daysLeft} days.</span>
            </div>
            <code className="GAtlas-settings-code">{cookieId || '—'}</code>
          </div>
        </section>

        <section className="GAtlas-settings-card">
          <h3>Connection</h3>
          <div className="GAtlas-settings-row">
            <div className="GAtlas-settings-row-label">
              <strong>Backend</strong>
              <span>Live health-check polling every 15 seconds.</span>
            </div>
            <span className={`GAtlas-status-chip ${backendStatus}`}>
              <span className={`GAtlas-data-dot ${backendStatus === 'online' ? '' : 'loading'}`}></span>
              {backendStatus === 'online' ? 'connected' : 'connecting…'}
            </span>
          </div>
        </section>
      </div>
    </div>
  );

  const renderHelp = () => {
    return (
      <div className="GAtlas-data-page">
        {renderPageBanner('book', 'Help & Docs',
          'How each part of G-GWAS works — pseudo-walkthroughs of the explorer, your GWAS workflow, the agent, the API, and how to reach us.')}

        <div className="GAtlas-help">
          <section className="GAtlas-help-section">
            <div className="GAtlas-help-section-head">
              <div className="GAtlas-help-section-icon"><i className="fas fa-globe"></i></div>
              <div>
                <h2>Global Atlas — explore biology</h2>
                <p>372 candidate genes from 1.7M scored variants, layered with Reactome biology, GWAS traits, loci, evidence, and top variants.</p>
              </div>
            </div>

            <div className="GAtlas-help-flow">
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">1</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Pick the layers you want</strong>
                  <div className="GAtlas-help-pseudo">
                    <span className="pseudo-label">Connect via</span>
                    <span className="pseudo-pill on" style={{ '--c': '#c084fc' }}><span className="dot"></span>Pathways</span>
                    <span className="pseudo-pill" style={{ '--c': '#facc15' }}><span className="dot"></span>Traits</span>
                    <span className="pseudo-pill" style={{ '--c': '#fb923c' }}><span className="dot"></span>Loci</span>
                    <span className="pseudo-pill" style={{ '--c': '#f472b6' }}><span className="dot"></span>Evidence</span>
                  </div>
                  <p>Toggle one or many — genes always sit at the center; the layers add hub nodes and edges.</p>
                </div>
              </div>

              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">2</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Search or filter to narrow the view</strong>
                  <div className="GAtlas-help-pseudo">
                    <span className="pseudo-search">
                      <i className="fas fa-search"></i>
                      <span>NFE2L2</span>
                      <span className="pseudo-filter-btn"><i className="fas fa-filter"></i></span>
                    </span>
                  </div>
                  <p>Type a gene / rsID / locus, or open the filters popover for axis &amp; evidence chips.</p>
                </div>
              </div>

              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">3</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Click a node — neighbors light up (1-hop full + 2-hop dim)</strong>
                  <div className="GAtlas-help-pseudo">
                    <span className="pseudo-graph">
                      <span className="pseudo-node center" style={{ background: '#5eead4' }}></span>
                      <span className="pseudo-node lit" style={{ background: '#c084fc' }}></span>
                      <span className="pseudo-node lit" style={{ background: '#facc15' }}></span>
                      <span className="pseudo-node dim" style={{ background: '#fb923c' }}></span>
                      <span className="pseudo-node dim" style={{ background: '#f472b6' }}></span>
                    </span>
                  </div>
                  <p>Right-side detail panel shows variants, loci, pathways, traits, and lets you jump between linked entities.</p>
                </div>
              </div>

              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">4</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Use the bottom-right rail to browse top hits</strong>
                  <div className="GAtlas-help-pseudo">
                    <span className="pseudo-rail">
                      <span className="pseudo-rail-tabs"><span className="active">Genes</span><span>Pathways</span><span>Traits</span></span>
                      <span className="pseudo-rail-row"><span className="dot" style={{ background: '#5eead4' }}></span>NFE2L2 · 35t</span>
                      <span className="pseudo-rail-row"><span className="dot" style={{ background: '#fb923c' }}></span>SBNO2 · 28t</span>
                    </span>
                  </div>
                  <p>Tabs auto-hide if their layer is off. Click any row to focus that node.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="GAtlas-help-section">
            <div className="GAtlas-help-section-head">
              <div className="GAtlas-help-section-icon"><i className="fas fa-upload"></i></div>
              <div>
                <h2>My GWAS — score your sumstats</h2>
                <p>Upload a TSV/TSV.gz of summary statistics (SNP, CHR, BP, P, BETA — or hm_*). It runs through predict.py on Vigil's GPU and is overlaid against the atlas.</p>
              </div>
            </div>

            <div className="GAtlas-help-flow">
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">1</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Drop a sumstats file</strong>
                  <div className="GAtlas-help-pseudo">
                    <span className="pseudo-drop">
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span>Drop GCST000612.txt.gz here</span>
                    </span>
                  </div>
                  <p>Direct upload to Cloudflare R2 — no proxy through our server, no 2 GB limit imposed.</p>
                </div>
              </div>

              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">2</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Job goes through the queue</strong>
                  <div className="GAtlas-help-pseudo">
                    <span className="pseudo-pipe">
                      <span className="pseudo-status" style={{ '--c': 'var(--status-warning)' }}>queued</span>
                      <i className="fas fa-arrow-right"></i>
                      <span className="pseudo-status" style={{ '--c': 'var(--status-info)' }}>running</span>
                      <i className="fas fa-arrow-right"></i>
                      <span className="pseudo-status" style={{ '--c': 'var(--status-success)' }}>done</span>
                    </span>
                  </div>
                  <p>Vigil's worker pulls 2 jobs at a time, runs predict.py, writes scored TSV back to R2.</p>
                </div>
              </div>

              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">3</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Right pane: interactive Manhattan + atlas overlap</strong>
                  <div className="GAtlas-help-pseudo">
                    <span className="pseudo-manhattan">
                      <span className="m-dot" style={{ left: '8%', top: '70%', background: '#5eead4' }}></span>
                      <span className="m-dot" style={{ left: '22%', top: '32%', background: '#fb923c' }}></span>
                      <span className="m-dot" style={{ left: '40%', top: '55%', background: '#90b4e8' }}></span>
                      <span className="m-dot" style={{ left: '60%', top: '20%', background: '#f472b6' }}></span>
                      <span className="m-dot" style={{ left: '78%', top: '60%', background: '#facc15' }}></span>
                      <span className="m-dot" style={{ left: '90%', top: '40%', background: '#c084fc' }}></span>
                    </span>
                  </div>
                  <p>Hover any point for rsID + chr:bp + score. Click → opens the variant in the Global Atlas.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="GAtlas-help-section">
            <div className="GAtlas-help-section-head">
              <div className="GAtlas-help-section-icon"><i className="fas fa-comments"></i></div>
              <div>
                <h2>Atlas Agent — drive the network with words</h2>
                <p>GPT-4o-mini-backed assistant that knows the atlas, pathways, traits, and the agent's available tools (toggle layers, set filters, focus a node).</p>
              </div>
            </div>

            <div className="GAtlas-help-flow">
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">1</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Click the chat fab in the network's bottom-left</strong>
                  <div className="GAtlas-help-pseudo">
                    <span className="pseudo-chat">
                      <span className="bubble user">show me NOTCH-related pathways</span>
                      <span className="bubble assistant">3 pathways found — Negative regulation of NOTCH4, Pre-NOTCH Processing, NOTCH1 ICD…</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">2</div>
                <div className="GAtlas-help-stepbody">
                  <strong>The agent emits structured actions</strong>
                  <div className="GAtlas-help-pseudo">
                    <code className="pseudo-code">{'{ "type": "set_layers", "layers": ["pathway"] }'}</code>
                    <code className="pseudo-code">{'{ "type": "select", "kind": "pathway", "label": "Negative regulation of NOTCH4 signaling" }'}</code>
                  </div>
                  <p>The UI dispatches each action — layers toggle, filters set, the node gets selected. Not just chat — it actually moves the network.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="GAtlas-help-section">
            <div className="GAtlas-help-section-head">
              <div className="GAtlas-help-section-icon"><i className="fas fa-code"></i></div>
              <div>
                <h2>API — programmatic access</h2>
                <p>JSON over HTTPS. Cookie-scoped. Documented in the <button className="linklike" onClick={() => setSelectedItem('api')}>API page</button> with curl + PowerShell examples and a "Try it" button per endpoint.</p>
              </div>
            </div>

            <div className="GAtlas-help-flow">
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">1</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Authenticate with your Webserver credential</strong>
                  <div className="GAtlas-help-pseudo">
                    <code className="pseudo-code">X-Webserver-Token: {cookieId || 'YOUR-CREDENTIAL'}</code>
                  </div>
                </div>
              </div>

              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum">2</div>
                <div className="GAtlas-help-stepbody">
                  <strong>Endpoint groups</strong>
                  <div className="GAtlas-help-pseudo pseudo-endpoints">
                    <span><b>/summary</b> · whole-atlas counts</span>
                    <span><b>/network</b> · gene + layer hubs</span>
                    <span><b>/gene/:g</b> · drill-down</span>
                    <span><b>{`${serviceApiBase(GWAS_SERVICE_CODE)}/*`}</b> · centralized upload, jobs, and results</span>
                    <span><b>/chat/*</b> · agent</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="GAtlas-help-section">
            <div className="GAtlas-help-section-head">
              <div className="GAtlas-help-section-icon"><i className="fas fa-keyboard"></i></div>
              <div>
                <h2>Shortcuts &amp; tips</h2>
                <p>Power-user tricks scattered through the UI.</p>
              </div>
            </div>

            <div className="GAtlas-help-flow">
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum"><i className="fas fa-search"></i></div>
                <div className="GAtlas-help-stepbody">
                  <strong>Top search bar = global jump</strong>
                  <p>Click any header search result and you land in Global Atlas with that node selected, the right layer auto-toggled, and the camera panned + zoomed to it.</p>
                </div>
              </div>
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum"><i className="fas fa-mouse-pointer"></i></div>
                <div className="GAtlas-help-stepbody">
                  <strong>Click a node → 2-hop highlight</strong>
                  <p>Direct neighbors render at full brightness, second-hop neighbors at 50%, everything else dims. Edges to direct neighbors thicken.</p>
                </div>
              </div>
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum"><i className="fas fa-table"></i></div>
                <div className="GAtlas-help-stepbody">
                  <strong>Evidence tables — click into atlas</strong>
                  <p>Mechanisms / Loci / Genes pages: row click opens that entity in Global Atlas. Each cell value (gene/locus/variant) is independently clickable too. Sort by clicking a column label; filter per-column with the filter input under each label.</p>
                </div>
              </div>
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum"><i className="fas fa-eye"></i></div>
                <div className="GAtlas-help-stepbody">
                  <strong>Halo on the network</strong>
                  <p>Genes with public functional support (MPRA / CRISPR / MPRAVarDB) get a gold halo ring around them in any view.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="GAtlas-help-section">
            <div className="GAtlas-help-section-head">
              <div className="GAtlas-help-section-icon"><i className="fas fa-database"></i></div>
              <div>
                <h2>What's in the atlas</h2>
                <p>Numbers behind the explorer.</p>
              </div>
            </div>

            <div className="GAtlas-help-flow">
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum"><i className="fas fa-dna"></i></div>
                <div className="GAtlas-help-stepbody">
                  <strong>1.73M variants · 244 loci · 372 candidate genes</strong>
                  <p>Built from 1,587 GWAS contexts. Variants ranked into score bands (low / moderate / high recurrence). Candidates pass evidence-disjoint locus convergence at 3.4× empirical fold enrichment.</p>
                </div>
              </div>
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum"><i className="fas fa-project-diagram"></i></div>
                <div className="GAtlas-help-stepbody">
                  <strong>Reactome integration</strong>
                  <p>140K gene-pathway mappings, 2,012 leaf pathways with at least one candidate gene. Top recurring biology you'll see: Neutrophil degranulation, RHOA GTPase cycle, mRNA splicing, hypoxia signaling, NOTCH variants.</p>
                </div>
              </div>
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum"><i className="fas fa-heart-pulse"></i></div>
                <div className="GAtlas-help-stepbody">
                  <strong>EBI GWAS Catalog</strong>
                  <p>89,981 studies imported. Each variant carries up to 36+ trait associations. Trait map shows which traits the candidate genes converge on.</p>
                </div>
              </div>
              <div className="GAtlas-help-step">
                <div className="GAtlas-help-stepnum"><i className="fas fa-flask"></i></div>
                <div className="GAtlas-help-stepbody">
                  <strong>External evidence layers</strong>
                  <p>Open Targets L2G + e2G + colocalization, eQTL Catalogue, ABC enhancer-gene, pQTL — all aggregated as the Evidence layer.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="GAtlas-help-section wide">
            <div className="GAtlas-help-section-head">
              <div className="GAtlas-help-section-icon"><i className="fas fa-headset"></i></div>
              <div>
                <h2>Contact</h2>
                <p>Found a bug, want a feature, want a custom integration with your dataset? Reach out.</p>
              </div>
            </div>

            <div className="GAtlas-help-contact">
              <a className="GAtlas-help-contact-card" href="mailto:hello@phenomelongevity.com">
                <i className="fas fa-envelope"></i>
                <div>
                  <strong>hello@phenomelongevity.com</strong>
                  <span>Direct line for questions, bugs, and partnerships.</span>
                </div>
                <i className="fas fa-arrow-right" style={{ marginLeft: 'auto', opacity: 0.5 }}></i>
              </a>
              <p className="muted">Please include a clear subject line, your cookie ID (visible in Settings), and the URL/screenshot if reporting a UI issue.</p>
            </div>
          </section>
        </div>
      </div>
    );
  };

  const renderApiPage = () => {
    const baseUrl = getApiBaseUrl();
    const cookie = cookieId || 'YOUR-COOKIE-ID';
    const copy = (text, idx) => {
      navigator.clipboard.writeText(text);
      setApiCopied(idx);
      setTimeout(() => setApiCopied(null), 1500);
    };
    const fmtCurl = (method, path, body) => {
      const headerParts = `-H "X-Webserver-Token: ${cookie}"`;
      if (method === 'GET' || method === 'DELETE') {
        const m = method === 'DELETE' ? '-X DELETE ' : '';
        return `curl -s ${m}${headerParts} "${baseUrl}${path}"`;
      }
      return `curl -s -X ${method} ${headerParts} -H "Content-Type: application/json" \\\n  -d '${body || '{}'}' \\\n  "${baseUrl}${path}"`;
    };
    const fmtPwsh = (method, path, body) => {
      const head = `-Headers @{ "X-Webserver-Token" = "${cookie}" }`;
      if (method === 'GET') {
        return `Invoke-RestMethod -Uri "${baseUrl}${path}" \`\n  ${head}`;
      }
      if (method === 'DELETE') {
        return `Invoke-RestMethod -Method Delete -Uri "${baseUrl}${path}" \`\n  ${head}`;
      }
      return `Invoke-RestMethod -Method ${method} -Uri "${baseUrl}${path}" \`\n  ${head} \`\n  -ContentType "application/json" -Body '${body || '{}'}'`;
    };
    const exampleFor = (method, path, body) => apiOs === 'windows' ? fmtPwsh(method, path, body) : fmtCurl(method, path, body);

    const tryEndpoint = async (idx, method, path, body) => {
      setApiTryState((prev) => ({ ...prev, [idx]: { loading: true, status: null, body: null } }));
      try {
        const opts = { method };
        if (body && (method === 'POST' || method === 'PUT')) {
          opts.headers = { 'Content-Type': 'application/json' };
          opts.body = body;
        }
        const r = await atlasFetch(`${baseUrl}${path}`, opts);
        const text = await r.text();
        let pretty = text;
        try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch {}
        // Cap to 6 KB so the UI doesn't choke on huge network responses
        if (pretty.length > 6000) pretty = pretty.slice(0, 6000) + '\n…(truncated)';
        setApiTryState((prev) => ({ ...prev, [idx]: { loading: false, status: r.status, body: pretty } }));
      } catch (err) {
        setApiTryState((prev) => ({ ...prev, [idx]: { loading: false, status: 0, body: `error: ${err.message}` } }));
      }
    };

    const sections = [
      {
        title: 'Atlas summary',
        desc: 'Whole-atlas counts (variants / loci / candidate genes), main convergence results and tier stack.',
        endpoints: [
          { method: 'GET', path: '/api/atlas/summary', auth: 'cookie', body: null,
            sample: '{"variantCounts":{"variants":1730224,"recurrent_variants":312794,…},\n "candidateCounts":{"locus_gene_candidates":448,"candidate_loci":244,…},\n "mainResults":[…], "tiers":[…]}' },
        ],
      },
      {
        title: 'Variants',
        desc: 'Filtered variant rows. Useful for querying recurrent variants in an axis or a score band.',
        endpoints: [
          { method: 'GET', path: '/api/atlas/variants?min_traits=3&min_score=0.1&max_score=0.5&discovery=1&candidates=1&limit=20', auth: 'cookie', body: null,
            sample: '{"rows":[{"snp":"rs2706113","chr":"2","bp":177224061,"locus_500kb":"2:177000000",\n  "n_traits":12,"max_score":0.226,"score_band":"moderate_0.2_0.5",\n  "primary_axis":"neural_enriched","top_gene":"NFE2L2",…}]}' },
        ],
      },
      {
        title: 'Locus / Gene / Variant detail',
        desc: 'Drill into a single locus, gene, or variant — same data the Global Atlas detail panel uses.',
        endpoints: [
          { method: 'GET', path: '/api/atlas/locus/2:177000000', auth: 'cookie', body: null,
            sample: '{"locus":"2:177000000","candidates":[…],"variants":[…]}' },
          { method: 'GET', path: '/api/atlas/gene/NFE2L2', auth: 'cookie', body: null,
            sample: '{"gene":"NFE2L2","candidates":[…],"variants":[…],"pathways":[…],"traits":[…]}' },
          { method: 'GET', path: '/api/atlas/variant/rs2706113', auth: 'cookie', body: null,
            sample: '{"variant":{…},"evidence":[…],"candidates":[…],"traits":[…]}' },
        ],
      },
      {
        title: 'Network',
        desc: 'The unified atlas network. Pass any combination of layers to overlay on the candidate-gene base.',
        endpoints: [
          { method: 'GET', path: '/api/atlas/network?layers=pathway&limit=30', auth: 'cookie', body: null,
            sample: '{"layers":["pathway"],"rows":402,"nodes":[{"id":"gene:NFE2L2",…},\n  {"id":"pathway:R-HSA-9818025",…}],"edges":[{"source":"gene:NFE2L2","target":"pathway:R-HSA-9818025","type":"in_pathway"}…]}' },
          { method: 'GET', path: '/api/atlas/network?layers=trait,evidence&limit=20', auth: 'cookie', body: null, sample: '(combined trait + evidence overlay)' },
          { method: 'GET', path: '/api/atlas/network?layers=pathway,trait,locus,evidence,variant&limit=20', auth: 'cookie', body: null, sample: '(everything)' },
        ],
      },
      {
        title: 'Reactome pathways',
        desc: 'Top recurring leaf pathways across the candidate gene set. Supports name search.',
        endpoints: [
          { method: 'GET', path: '/api/atlas/pathways/top?limit=15', auth: 'cookie', body: null,
            sample: '{"rows":[{"pathway_id":"R-HSA-9006934","pathway_name":"Signaling by Receptor Tyrosine Kinases",\n  "recurring_genes":11,"total_genes_in_pathway":520,"gene_list":"NFE2L2,…"}]}' },
          { method: 'GET', path: '/api/atlas/pathways/top?q=NOTCH&limit=8&min_overlap=1', auth: 'cookie', body: null, sample: '(name-filtered hits)' },
          { method: 'GET', path: '/api/atlas/gene/NFE2L2/pathways', auth: 'cookie', body: null, sample: '{"gene":"NFE2L2","pathways":[…]}' },
        ],
      },
      {
        title: 'GWAS jobs (My GWAS)',
        desc: `My GWAS uses the centralized XOmics lifecycle at ${serviceApiBase(GWAS_SERVICE_CODE)}. The screen derives its pipeline, input contract and accepted sample types from the active release; new uploads are disabled while the release declares no sample type for the pipeline.`,
        endpoints: [],
      },
      {
        title: 'Atlas Agent (chat)',
        desc: 'GPT-4o-mini-backed agent. Returns answer + structured actions (set_layers, select, …) the UI dispatches.',
        endpoints: [
          { method: 'POST', path: '/api/atlas/chat/message', auth: 'cookie',
            body: '{"message":"show NFE2L2","context":{"active_layers":["pathway"]}}',
            sample: '{"answer":"NFE2L2 hits insulin and CHD…",\n "actions":[{"type":"set_layers","layers":["pathway","trait"]},\n  {"type":"select","kind":"gene","label":"NFE2L2"}],\n "tools":{…},"model":"gpt-4o-mini"}' },
          { method: 'GET', path: '/api/atlas/chat/history?limit=20', auth: 'cookie', body: null, sample: '{"rows":[{"role":"user","content":"…"},{"role":"assistant","content":"…"}]}' },
          { method: 'DELETE', path: '/api/atlas/chat/history', auth: 'cookie', body: null, sample: '{"deleted":12,"cookie_id":"…"}' },
        ],
      },
    ];

    const errorCodes = [
      { code: '400', desc: 'Bad request — missing or invalid parameter' },
      { code: '401', desc: 'Unauthorized — only relevant on internal endpoints' },
      { code: '404', desc: 'Resource not found (locus / gene / variant / job)' },
      { code: '409', desc: 'Conflict (job not ready, file not on R2, status conflict)' },
      { code: '500', desc: 'Server error — see server logs' },
    ];

    let copyIdx = 0;
    return (
      <div className="GAtlas-data-page">
        {renderPageBanner('code', 'API',
          'Programmatic access to the G-GWAS knowledge base, network layers, GWAS jobs, and the chat agent. All endpoints are JSON over HTTPS, scoped to your browser cookie.')}

        <div className="GAtlas-api">
          <div className="GAtlas-api-section">
            <h2>Authentication</h2>
            <p className="GAtlas-api-description">
              Every request needs your workspace credential in the <code>X-Webserver-Token</code> header. It is registered centrally on first visit and scopes Atlas chat history to that credential.
            </p>
            <pre className="GAtlas-api-code GAtlas-api-code-inline">X-Webserver-Token: {cookie}</pre>
            <p className="GAtlas-api-note">Atlas reads and chat history are scoped to a registered Webserver credential. My GWAS uses the same credential and centralized lifecycle.</p>
          </div>

          <div className="GAtlas-api-os-toggle">
            <span className="GAtlas-api-os-label">Examples</span>
            <div className="GAtlas-api-os-buttons">
              <button className={`GAtlas-api-os-btn ${apiOs === 'linux' ? 'active' : ''}`} onClick={() => setApiOs('linux')}>
                <i className="fab fa-linux"></i> Linux / macOS
              </button>
              <button className={`GAtlas-api-os-btn ${apiOs === 'windows' ? 'active' : ''}`} onClick={() => setApiOs('windows')}>
                <i className="fab fa-windows"></i> Windows
              </button>
            </div>
          </div>

          {sections.map((sec, sIdx) => (
            <div key={sec.title} className="GAtlas-api-section">
              <h2>{sec.title}</h2>
              <p className="GAtlas-api-description">{sec.desc}</p>
              {sec.endpoints.map((ep, eIdx) => {
                const idx = `${sIdx}-${eIdx}`;
                const code = exampleFor(ep.method, ep.path, ep.body);
                const tryRes = apiTryState[idx];
                return (
                  <div key={idx} className="GAtlas-api-endpoint-block">
                    <div className="GAtlas-api-endpoint-line">
                      <span className={`GAtlas-method ${ep.method.toLowerCase()}`}>{ep.method}</span>
                      <code>{ep.path}</code>
                    </div>
                    <div className="GAtlas-api-example">
                      <div className="GAtlas-api-example-header">
                        <span className="GAtlas-api-example-label">{apiOs === 'windows' ? 'PowerShell' : 'curl'}</span>
                        <div className="GAtlas-api-example-actions">
                          <button
                            className={`GAtlas-copy-btn ${apiCopied === idx ? 'copied' : ''}`}
                            onClick={() => copy(code, idx)}
                          >
                            <i className={`fas ${apiCopied === idx ? 'fa-check' : 'fa-copy'}`}></i>
                            {apiCopied === idx ? 'Copied' : 'Copy'}
                          </button>
                          <button
                            className="GAtlas-copy-btn"
                            onClick={() => tryEndpoint(idx, ep.method, ep.path, ep.body)}
                            disabled={tryRes?.loading}
                            title="Run the request and show the live response"
                          >
                            <i className={`fas ${tryRes?.loading ? 'fa-spinner fa-spin' : 'fa-bolt'}`}></i>
                            {tryRes?.loading ? 'Running…' : 'Try it'}
                          </button>
                        </div>
                      </div>
                      <pre className="GAtlas-api-code">{code}</pre>
                      {tryRes && tryRes.body !== null && (
                        <div className="GAtlas-api-tryresult">
                          <div className="GAtlas-api-example-header">
                            <span className="GAtlas-api-example-label">
                              Live response · <span className={`GAtlas-status-code code-${String(tryRes.status).startsWith('2') ? '200' : tryRes.status}`}>{tryRes.status || 'err'}</span>
                            </span>
                          </div>
                          <pre className="GAtlas-api-code">{tryRes.body}</pre>
                        </div>
                      )}
                      {ep.sample && !tryRes && (
                        <details className="GAtlas-api-sample">
                          <summary>Example response</summary>
                          <pre className="GAtlas-api-code">{ep.sample}</pre>
                        </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <div className="GAtlas-api-section">
            <h2>Status codes</h2>
            <table className="GAtlas-api-table">
              <thead><tr><th>Code</th><th>Meaning</th></tr></thead>
              <tbody>
                {errorCodes.map((e) => (
                  <tr key={e.code}>
                    <td><span className={`GAtlas-status-code code-${e.code}`}>{e.code}</span></td>
                    <td>{e.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (selectedItem) {
      case 'atlas':
        return renderAtlas();
      case 'upload':
        return renderMyGwas();
      case 'mechanisms':
        return renderMechanisms();
      case 'loci':
        return renderLoci();
      case 'genes':
        return renderGenes();
      case 'api':
        return renderApiPage();
      case 'help':
        return renderHelp();
      case 'settings':
        return renderSettings();
      case 'home':
      default:
        return renderHome();
    }
  };

  return (
    <div className="GAtlas-shell">
      <aside className="GAtlas-sidebar">
        <a href="/" className="GAtlas-sidebar-header">
          <img src={baseLogo} alt="Home" className="GAtlas-logo GAtlas-logo-dark" />
          <img src={baseLogoLight} alt="Home" className="GAtlas-logo GAtlas-logo-light" />
        </a>

        <div className="GAtlas-sidebar-menu">
          {sidebarGroups.map((group, groupIndex) => (
            <div key={group.id} className="GAtlas-sidebar-group">
              <div className="GAtlas-sidebar-group-title">{group.title}</div>
              <div className="GAtlas-sidebar-group-items">
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    className={`GAtlas-sidebar-item ${selectedItem === item.id ? 'GAtlas-sidebar-item-active' : ''}`}
                    onClick={() => setSelectedItem(item.id)}
                  >
                    <i className={`fas fa-${item.icon} GAtlas-sidebar-icon`}></i>
                    <span className="GAtlas-sidebar-label">{item.label}</span>
                  </button>
                ))}
              </div>
              {groupIndex < sidebarGroups.length - 1 && <div className="GAtlas-sidebar-divider"></div>}
            </div>
          ))}
        </div>

        <div className="GAtlas-sidebar-footer">
          <div className="GAtlas-cookie-icon" data-tooltip={`ID: ${cookieId || 'pending'}\n${daysLeft} days left`}>
            <i className="fas fa-cookie-bite"></i>
          </div>
        </div>
      </aside>

      <main className="GAtlas-main">
        <header className="GAtlas-content-header">
          <div className="GAtlas-search-container">
            <input
              type="text"
              className="GAtlas-header-search"
              placeholder="Search atlas, loci, genes, variants, pathways, traits…"
              value={headerQuery}
              onFocus={() => setSearchOpen(true)}
              onChange={(e) => { setHeaderQuery(e.target.value); setSearchOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); } }}
            />
            <i className="fas fa-search GAtlas-search-icon"></i>
            <GAtlasSearchModal
              open={searchOpen}
              query={headerQuery.trim()}
              onClose={() => setSearchOpen(false)}
              onPick={(r) => {
                let sel = null;
                if (r.kind === 'gene') sel = { kind: 'gene', id: r.label, axis: r.axis, nodeId: `gene:${r.label}` };
                else if (r.kind === 'locus') sel = { kind: 'locus', id: r.label, nodeId: `locus:${r.label}` };
                else if (r.kind === 'variant') sel = { kind: 'variant', id: r.label, nodeId: `variant:${r.label}` };
                else if (r.kind === 'pathway' && r.pathway_id) sel = { kind: 'pathway', id: r.label, nodeId: `pathway:${r.pathway_id}` };
                else if (r.kind === 'trait' && r.gcst_id) sel = { kind: 'trait', id: r.label, gcst: r.gcst_id, nodeId: `trait:${r.gcst_id}` };
                if (sel) {
                  setSelection(sel);
                  setSelectedItem('atlas');
                }
                setSearchOpen(false);
                setHeaderQuery('');
              }}
            />
          </div>
          <div className="GAtlas-header-buttons">
            <button className={`GAtlas-header-btn ${selectedItem === 'home' ? 'GAtlas-header-btn-active' : ''}`} onClick={() => setSelectedItem('home')}>
              <i className="fas fa-home"></i><span>Home</span>
            </button>
            <button className={`GAtlas-header-btn ${selectedItem === 'api' ? 'GAtlas-header-btn-active' : ''}`} onClick={() => setSelectedItem('api')}>
              <i className="fas fa-code"></i><span>API</span>
            </button>
            <button className={`GAtlas-header-btn ${selectedItem === 'settings' ? 'GAtlas-header-btn-active' : ''}`} onClick={() => setSelectedItem('settings')}>
              <i className="fas fa-cog"></i><span>Settings</span>
            </button>
          </div>
        </header>

        <section className="GAtlas-content-wrapper">
          {renderContent()}
        </section>
      </main>
    </div>
  );
}

export default GAtlas;
